// ============================================================
// SHARED DTOs — Zod schemas reused by web SPA + api Worker
// Story 1.8 — spec: docs/architecture.md §7.2 (Uploads) + §6.3 (Upload Flow)
//
// Single source of truth for the upload presign contract. The Worker validates
// inbound requests against `PresignSchema`; the SPA (Epic 4) imports the
// inferred `PresignRequest` type so request building and validation stay in
// lockstep. New DTOs from future epics get appended here.
// ============================================================
import { z } from 'zod'

// SEM a extensão '.ts' DE PROPÓSITO, ao contrário do `index.ts` ao lado: este
// ficheiro é compilado também pelo programa do `@caliber/render-shared`, que o
// importa como `@caliber/shared/dtos` e cujo tsconfig NÃO liga
// `allowImportingTsExtensions` — com a extensão, `pnpm typecheck` falha lá com
// TS5097. A resolução 'Bundler' do tsconfig base resolve `./cnpj` → `./cnpj.ts`.
import { isValidCnpj } from './cnpj'

// ---- Presign ----

/**
 * Allowlisted content types for browser-direct R2 uploads.
 * Strict allowlist (no wildcards) — see Quality Gates §1.8.
 *
 * Video: `mp4` and `webm` (interview recordings)
 * User transcript: `text/plain`, `text/markdown`, `text/vtt`
 * Repo archive: `application/gzip`, `application/zip`
 * Assessment statement (Story 13.2): `application/pdf`, `.docx`
 */
export const PRESIGN_CONTENT_TYPES = [
  'video/mp4',
  'video/webm',
  'text/plain',
  'text/markdown',
  'text/vtt',
  'application/gzip',
  'application/zip',
  // Story 13.2 (Epic 13) — enunciado da prova prática (PDF / DOCX).
  // `extensionFor()` em r2.ts já mapeia estes tipos desde a Story 12.1.
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const

/**
 * Purpose classes — each maps to a distinct sub-path under
 * `evaluations/<eval-id>/input/` and to a distinct soft size limit.
 */
export const PRESIGN_PURPOSES = [
  'video',
  'user-transcript',
  'repo-archive',
  // Story 13.2 (Epic 13) — enunciado da prova prática. NÃO mapeia para
  // `evaluations/<eval-id>/input/`: a chave é construída inline no handler de
  // uploads sob o prefixo PERSISTENTE `assessments/<id>/statement/`.
  'assessment-statement',
] as const

/**
 * Soft size limits per purpose (bytes). Enforced server-side at presign time.
 *  - video                → 2 GiB  (2 147 483 648)
 *  - user-transcript      → 500 KiB (524 288)
 *  - repo-archive         → 100 MiB (104 857 600)
 *  - assessment-statement → 20 MiB  (20 971 520) — PDF/DOCX do enunciado
 */
export const PRESIGN_MAX_SIZE_BYTES = {
  video: 2_147_483_648,
  'user-transcript': 524_288,
  'repo-archive': 104_857_600,
  'assessment-statement': 20_971_520,
} as const satisfies Record<(typeof PRESIGN_PURPOSES)[number], number>

export const PresignSchema = z.object({
  contentType: z.enum(PRESIGN_CONTENT_TYPES),
  sizeBytes: z.number().int().positive(),
  purpose: z.enum(PRESIGN_PURPOSES),
  evaluationId: z.string().min(1).optional(),
  // Story 13.2 — exigido pelo handler quando purpose === 'assessment-statement'
  // (constrói a chave R2 `assessments/<assessmentId>/statement/...`).
  assessmentId: z.string().min(1).optional(),
})

export type PresignRequest = z.infer<typeof PresignSchema>

export interface PresignResponse {
  /** R2 presigned PUT URL — short-lived (15 min default). */
  uploadUrl: string
  /** Object key inside the R2 bucket. Determines later GET/HEAD paths. */
  r2Key: string
  /** ISO-8601 UTC timestamp marking URL expiry. */
  expiresAt: string
  /** Soft limit echoed back so clients can verify before PUT. */
  maxSizeBytes: number
}

// ============================================================
// ASSESSMENTS (dynamic interviews) — Story 2.1
// Source of truth: caliber-web/docs/architecture.md §7.3
// Schemas reused by api Worker (validation) and web SPA (typing).
// ============================================================

/**
 * RubricLevel — one row of a per-question evaluation rubric (Story 2.6).
 * Spec: docs/architecture.md §7.3 (Schemas Zod) + §5.1 (`interview_questions.rubric`
 * JSON column). Shared so the web SPA (Story 2.6 editor) and the api Worker
 * (Story 2.1 PATCH validation) agree on the contract byte-for-byte.
 *
 *  - `score`: integer 0..10 — must be unique within a rubric (see `RubricSchema`).
 *  - `label`: short human-readable name for the level (1..100 chars).
 *  - `criteria`: descriptive text of what earns this level (1..500 chars).
 *
 * Order is implicit (array order = level order).
 */
export const RubricLevelSchema = z.object({
  score: z.number().int().min(0).max(10),
  label: z.string().min(1).max(100),
  criteria: z.string().min(1).max(500),
})
export type RubricLevel = z.infer<typeof RubricLevelSchema>

/**
 * RubricSchema — a full rubric: an ordered array of at least 2 levels with
 * unique scores. Validated server-side before persisting and client-side
 * before the PATCH fires (Story 2.6 AC11, AC12).
 */
export const RubricSchema = z
  .array(RubricLevelSchema)
  .min(2, 'Rubric precisa de pelo menos 2 níveis')
  .refine(
    (levels) => new Set(levels.map((l) => l.score)).size === levels.length,
    'Scores devem ser únicos',
  )

export type Rubric = z.infer<typeof RubricSchema>

/**
 * Slug constraint shared by create + import flows.
 * Lowercase letters, digits and hyphens — no leading/trailing hyphen enforced upstream.
 */
const SLUG_REGEX = /^[a-z0-9-]+$/
export const SlugSchema = z
  .string()
  .min(1)
  .max(80)
  .regex(SLUG_REGEX, 'Slug must be lowercase letters, digits and hyphens only')

export const CreateAssessmentSchema = z.object({
  name: z.string().min(1).max(200),
  description: z.string().optional(),
  /** Optional — auto-generated from `name` (slugify) when absent. */
  slug: SlugSchema.optional(),
  /**
   * Story 2.15 — full extracted text of an imported job-posting document.
   * Optional: only sent when the assessment was created via document import.
   * Stored verbatim in `assessments.job_description_source` for traceability.
   */
  jobDescriptionSource: z.string().optional(),
})
export type CreateAssessment = z.infer<typeof CreateAssessmentSchema>

/**
 * Duplicar um modelo de ENTREVISTA — só o nome da cópia vem do cliente.
 *
 * O slug não é aceite aqui de propósito. Em `POST /` o slug é um campo que o
 * usuário vê e edita, então um 409 por colisão é acionável: ele muda o campo.
 * Quem duplica nunca escreveu slug nenhum — devolver-lhe 409 seria pedir que
 * resolvesse uma colisão que não criou. `POST /:id/duplicate` deriva o slug do
 * nome e uniquifica-o no servidor (ver a rota).
 */
export const DuplicateAssessmentSchema = z
  .object({
    name: z.string().min(1).max(200),
  })
  .strict()
export type DuplicateAssessment = z.infer<typeof DuplicateAssessmentSchema>

/**
 * Partial update — `type` and `id` are deliberately omitted (immutable after create).
 */
export const UpdateAssessmentSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().nullable().optional(),
    slug: SlugSchema.optional(),
    enabled: z.boolean().optional(),
    /**
     * Story 2.15 — updated job-posting source text. Declared inside the
     * `.object({})` (BEFORE `.strict()`) so the strict shape accepts the
     * key. `nullable()` lets a caller clear a previously-imported source.
     */
    jobDescriptionSource: z.string().nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateAssessment = z.infer<typeof UpdateAssessmentSchema>

// ---- Topics ----

export const CreateTopicSchema = z.object({
  name: z.string().min(1).max(200),
  weight: z.number().positive().default(1),
  order: z.number().int().nonnegative().optional(),
})
export type CreateTopic = z.infer<typeof CreateTopicSchema>

export const UpdateTopicSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    weight: z.number().positive().optional(),
    order: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateTopic = z.infer<typeof UpdateTopicSchema>

// ---- Questions ----

// `questionNumber` NÃO entra nestes dois schemas: o código ('A1', 'B2') é
// derivado da POSIÇÃO e gravado pelo SERVIDOR — ver `lib/renumber-questions.ts`
// e `numbering.ts`. Um cliente que ainda o envie no POST tem a chave DESCARTADA
// em silêncio (este schema não é `.strict()`), que é o comportamento desejado
// durante a transição; no PATCH, que é `.strict()`, enviá-lo passa a ser 400.
export const CreateInterviewQuestionSchema = z.object({
  topicId: z.string().min(1),
  canonicalText: z.string().min(1),
  weight: z.number().positive().default(1),
  /** Minimum 2 rubric levels (e.g. 0 and 10) — §7.3. */
  rubric: z.array(RubricLevelSchema).min(2),
  evaluatorNotes: z.string().optional(),
  // ⚠️ AQUI ESTEVE `paraphrases` — um array de conveniência, que a rota
  // materializava em linhas de `interview_question_paraphrases`. Saiu a
  // 2026-09-04 com a tabela (migration 0046).
  //
  // O comentário acima diz o que acontece a um cliente que ainda o envie, e a
  // resposta é a MESMA do `questionNumber`: este schema não é `.strict()`,
  // portanto a chave é DESCARTADA em silêncio. Um SPA por atualizar continua a
  // criar perguntas — só não cria paráfrases, que é o pretendido.
  order: z.number().int().nonnegative().optional(),
})
export type CreateInterviewQuestion = z.infer<typeof CreateInterviewQuestionSchema>

export const UpdateInterviewQuestionSchema = z
  .object({
    topicId: z.string().min(1).optional(),
    canonicalText: z.string().min(1).optional(),
    weight: z.number().positive().optional(),
    rubric: z.array(RubricLevelSchema).min(2).optional(),
    evaluatorNotes: z.string().nullable().optional(),
    order: z.number().int().nonnegative().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateInterviewQuestion = z.infer<typeof UpdateInterviewQuestionSchema>

// ---- Assistente de IA ----
//
// Contrato do `POST /api/assessments/:id/ai-assist` — T0 do plano de
// 2026-09-03 (`docs/superpowers/plans/2026-09-03-assistente-de-ia-ondas.md`).
// Vive aqui, e não numa cópia por lado, porque o web precisa da união
// discriminada para tipar `current`/`onApply` e o worker precisa do MESMO
// schema para validar. Duas cópias divergiriam em silêncio — é a razão pela
// qual `RubricLevelSchema` já é partilhado, e é a mesma aqui.
//
// ⚠️ A REGRA QUE GOVERNA TUDO O QUE VEM A SEGUIR: o corpo leva REFERÊNCIAS
// (ids), nunca um `context` montado pelo cliente. Um `context` livre faria
// deste endpoint um proxy de LLM autenticado — qualquer texto entraria no
// prompt, e o gate de admin limita QUEM abusa, não DE QUÊ. O envolvente
// (nome do tópico, perguntas irmãs, níveis irmãos, descrição do modelo) é
// buscado no D1 pelo servidor, a partir dos ids (ver `lib/assist-context.ts`).
// O único texto livre é a `instruction` e o `current` — e o `current` só
// existe porque pode ser um valor ainda NÃO SALVO, que o servidor não tem
// como conhecer. AMBOS têm teto: a `instruction` por `AI_ASSIST_INSTRUCTION_MAX`,
// o `current` por escopo (`aiAssistOversize`). Um texto livre sem teto que
// entra no prompt verbatim é a mesma porta, aberta pelo outro lado.

/**
 * Teto da instrução do usuário — 2000 caracteres.
 *
 * ⚠️ NÃO é um `.max()` no schema, e isso é deliberado: um `.max()` faria o
 * `problemFromZod` devolver 400, e o contrato pede 413 (payload grande demais).
 * O molde é o `EXTRACT_MAX_TEXT_LENGTH` do `POST /extract` — constante aqui,
 * `if` explícito na rota.
 */
export const AI_ASSIST_INSTRUCTION_MAX = 2000

/**
 * Tetos de texto usados pelo CLAMP do assistente — nunca devolver ao cliente
 * algo que o `PATCH` seguinte teria de recusar.
 *
 * Medidos contra o D1 de produção a 2026-09-03 (40 perguntas reais):
 * `canonical_text` máx 439, `evaluator_notes` máx 474 — zero linhas acima de
 * 600. 1000 é ~2,2× o maior valor real, e já é um número desta base de código
 * (é o teto que o `POST /extract` aplica ao `description`).
 *
 * ⚠️ DE PROPÓSITO NÃO LIGADOS a `CreateInterviewQuestionSchema` /
 * `UpdateInterviewQuestionSchema`: pôr `.max(1000)` lá faria o `PATCH` passar
 * a recusar textos que hoje aceita. Nenhuma linha existente seria afetada, mas
 * é uma porta que só o owner fecha. Enquanto ele não decidir, estas constantes
 * governam SÓ o assistente, e o `PATCH` continua sem teto.
 */
export const CANONICAL_TEXT_MAX = 1000
export const EVALUATOR_NOTES_MAX = 1000

/**
 * Teto do nome de tópico no clamp do assistente — decisão D2 do plano.
 *
 * `CreateTopicSchema`/`UpdateTopicSchema` dizem 200, mas `topic-editor.tsx`
 * bloqueia acima de 100 na tela. O assist usa **o mais apertado**: 100 é o que
 * o usuário consegue voltar a gravar pela tela. Maior nome real em produção:
 * 40 caracteres.
 */
export const TOPIC_NAME_MAX = 100

/**
 * Os DOIS tetos da rubrica de um RASCUNHO — contagem de níveis e texto total.
 *
 * ⚠️ PORQUE SÃO DOIS. O `current` do escopo `question` é o único que traz um
 * ARRAY, e o array mede-se em duas dimensões independentes: muitos níveis
 * curtos, ou poucos níveis enormes. Um teto só de contagem deixa passar
 * 2 níveis de 1 MB; um teto só de texto deixa passar 100 000 níveis vazios.
 * Ambos acabam no user prompt VERBATIM (`formatarRascunho`), e o array do
 * rascunho — ao contrário do `RubricLevelSchema` — não tem `.max()` nenhum a
 * limitá-lo (ver `AiAssistRubricLevelDraftSchema`).
 *
 * ⚠️ PORQUE NÃO SÃO `.max()` NO SCHEMA: a mesma doutrina da `instruction` e do
 * `current` de texto — um `.max()` faz o `problemFromZod` devolver 400, e a
 * resposta certa a "grande demais" é 413. Constante aqui, `if` explícito em
 * `aiAssistOversize`.
 *
 * De onde saem os números, para não serem palpite:
 *   - `score` é `int 0..10` e os scores têm de ser únicos ao salvar → uma
 *     rubrica persistível tem no máximo 11 níveis. 20 dá folga a um rascunho
 *     com duplicados transitórios, que o editor permite enquanto se escreve.
 *   - 12 000 = 20 × 600, e 600 = 100 (`label`) + 500 (`criteria`), os tetos do
 *     `RubricLevelSchema`. Isto é: o maior rascunho que ainda pode vir a ser
 *     salvo, inteiro, cabe com folga.
 */
export const AI_ASSIST_RUBRIC_LEVELS_MAX = 20
export const AI_ASSIST_RUBRIC_CHARS_MAX = 12_000

/**
 * Os CINCO escopos. A ORDEM é contrato: o teste de T0 afirma-a, e os prompts, a
 * busca de contexto e a rota casam por esta grafia exata.
 *
 * Estão todos aqui desde o primeiro dia, e o agrupamento (campos de uma
 * pergunta, a pergunta inteira, o tópico) é o que torna a lista legível. Podem
 * mudar de lugar sem consequência: o único consumidor da POSIÇÃO é o teste que
 * afirma esta lista; tudo o resto indexa pela grafia
 * (`AiAssistTargetSchema.enum[…]`, e o `FORMA_POR_TARGET` do SPA, que é um
 * `Record`). A anotação desse `Record` é o que obriga quem acrescentar um
 * escopo AQUI a declarar lá a largura e as colunas — e é ela que, nas duas
 * remoções abaixo, obrigou o SPA a LARGAR a chave que saiu.
 *
 * ⚠️ LÁPIDE (1/2) — `question.paraphrases` foi o quarto escopo entre 2026-09-03 e
 * 2026-09-04, e saiu com a tabela `interview_question_paraphrases` (migration
 * 0046). Ficam registados aqui os nomes que desapareceram com ele, porque são
 * por eles que se procura: `ParaphrasesRefSchema`,
 * `AiAssistParaphrasesProposalSchema`, `AI_ASSIST_PARAPHRASES_MAX`,
 * `PARAPHRASE_TEXT_MAX` e `CreateParaphraseSchema`. Isto era um escopo de IA
 * que gerava reformulações de um enunciado para casar a transcrição contra a
 * pergunta certa; a detecção que as consumia passou a ser feita por LLM sem
 * elas, e a tabela tinha ZERO linhas em produção.
 *
 * ⚠️ LÁPIDE (2/2) — `topic.new` foi o sexto escopo, e saiu a 2026-09-05. O nome
 * que desapareceu com ele, porque é por ele que se procura: `TopicNewRefSchema`.
 *
 * NÃO saiu pelo motivo das paráfrases: nada foi apagado do D1 por causa dele.
 * Saiu por ficar sem CHAMADOR — o gatilho vivia ao lado de «Novo tópico» na
 * barra lateral (`assessment-sidebar.tsx`) e foi retirado a pedido do dono do
 * produto. Um escopo que nenhuma tela invoca não fica inofensivo por isso:
 * continua a aceitar o pedido de quem o forje, e a gastar o modelo pago.
 *
 * O que ele fazia — criar um tópico DO ZERO, com as perguntas todas, numa
 * escrita só — deixa de estar disponível; o `topic` que fica reescreve um
 * tópico que JÁ EXISTE. Eram duas variantes, não um campo e o seu dono: dois
 * `ref` (um com `topicId`, o outro vazio) e dois destinos de escrita, que a
 * união discriminada impedia de se trocarem.
 */
export const AiAssistTargetSchema = z.enum([
  'question.canonicalText',
  'question.evaluatorNotes',
  'question.rubricLevel',
  'question',
  'topic',
])
export type AiAssistTarget = z.infer<typeof AiAssistTargetSchema>

/**
 * `edit` leva o valor atual como base; `create` escreve de novo e o valor
 * atual NÃO entra no prompt (o envolvente entra sempre — sem ele a IA repete
 * as perguntas irmãs).
 */
export const AiAssistModeSchema = z.enum(['edit', 'create'])
export type AiAssistMode = z.infer<typeof AiAssistModeSchema>

/**
 * Um nível de rubrica COMO RASCUNHO — o que está no estado local do editor,
 * que não é o que se salva.
 *
 * ⚠️ PORQUE EXISTE, apesar de T0 do plano dizer "nenhum schema novo de rubric"
 * (linha 488): aquela cláusula governa a PROPOSTA e a PERSISTÊNCIA, e continua
 * de pé — a proposta reusa `RubricSchema`, e o `PATCH` reusa `RubricLevelSchema`.
 * Este schema nasceu do gate da onda 2, que mediu o custo de os confundir:
 * «Adicionar nível» acrescenta `{ score, label: '', criteria: '' }` ao estado
 * do editor e esse nível NUNCA é PATCHado (`question-editor.tsx:271-275`), por
 * desenho. Validar o rascunho com o schema de persistência fazia o pedido
 * seguinte responder 400 com a mensagem crua do Zod — num caminho normal, e
 * igual para quem limpa um `criteria` para o reescrever.
 *
 * ⚠️ `label`/`criteria` são `z.string()` NU: sem `.min(1)` (é rascunho) e sem
 * `.max()` (grande demais é 413, não 400 — ver `AI_ASSIST_RUBRIC_CHARS_MAX`,
 * que é quem impõe o teto). O `score` reusa o do `RubricLevelSchema`: 0..10
 * continua a ser 0..10, mesmo em rascunho.
 *
 * Não é `.strict()`, como o `RubricLevelSchema` também não é: apertar aqui
 * fabricaria um 400 novo exatamente no caminho que este schema existe para
 * desbloquear.
 */
export const AiAssistRubricLevelDraftSchema = z.object({
  score: RubricLevelSchema.shape.score,
  label: z.string(),
  criteria: z.string(),
})
export type AiAssistRubricLevelDraft = z.infer<typeof AiAssistRubricLevelDraftSchema>

// O `current` de um rascunho NÃO leva `.max()` nos textos, nem `.min(2)` na
// rubric, nem teto ao comprimento dela — mas isso NÃO quer dizer que não tenha
// teto. Os tetos existem, vivem em `aiAssistOversize` (no fim desta secção) e a
// rota impõe-nos com 413, pela mesma razão que a `instruction`: um `.max()`
// aqui daria 400, e "grande demais" não é "malformado". Ver o cabeçalho de
// `aiAssistOversize`.
export const AiAssistQuestionDraftSchema = z
  .object({
    canonicalText: z.string(),
    evaluatorNotes: z.string().nullable(),
    rubric: z.array(AiAssistRubricLevelDraftSchema),
    /**
     * Decisão de negócio, não de redação — está aqui só porque este schema
     * espelha o estado do editor (é `.strict()`, como o resto do rascunho).
     *
     * ⚠️ A ROTA NUNCA O LÊ. `AiAssistQuestionProposalSchema` (a proposta que o
     * `.strict()` aceita de volta do modelo) não tem `weight` nenhum, e
     * `perguntaAparada()` (routes/assessments.ts, ramo `question` de
     * `planearAssist`) monta a proposta campo a campo — `canonicalText`,
     * `evaluatorNotes`, `rubric` — sem nunca ler `ref.current.weight`. O peso
     * fica onde já estava gravado; ninguém o repõe a partir daqui.
     */
    weight: z.number().positive(),
  })
  .strict()
export type AiAssistQuestionDraft = z.infer<typeof AiAssistQuestionDraftSchema>

// O tópico não leva as perguntas irmãs: essas vêm do D1, pelo `topicId`.
export const AiAssistTopicDraftSchema = z
  .object({
    name: z.string(),
    weight: z.number().positive(),
  })
  .strict()
export type AiAssistTopicDraft = z.infer<typeof AiAssistTopicDraftSchema>

// Um ramo de `ref` por target. `.strict()` em cada um: uma chave a mais é
// rejeitada, não ignorada.
const CanonicalTextRefSchema = z
  .object({ questionId: z.string().min(1), current: z.string().nullable() })
  .strict()

const EvaluatorNotesRefSchema = z
  .object({ questionId: z.string().min(1), current: z.string().nullable() })
  .strict()

const RubricLevelRefSchema = z
  .object({
    questionId: z.string().min(1),
    /** Reusa o `score` do `RubricLevelSchema` — não redeclarar 0..10. */
    score: RubricLevelSchema.shape.score,
    current: RubricLevelSchema.nullable(),
  })
  .strict()

const QuestionRefSchema = z
  .object({ questionId: z.string().min(1), current: AiAssistQuestionDraftSchema.nullable() })
  .strict()

const TopicRefSchema = z
  .object({ topicId: z.string().min(1), current: AiAssistTopicDraftSchema.nullable() })
  .strict()

/**
 * A união dos cinco `ref`. Existe para dar o tipo `AiAssistRef`; a
 * DISCRIMINAÇÃO real acontece no `AiAssistRequestSchema`, porque o `ref` não
 * tem chave `target` por onde discriminar.
 */
export const AiAssistRefSchema = z.union([
  CanonicalTextRefSchema,
  EvaluatorNotesRefSchema,
  RubricLevelRefSchema,
  QuestionRefSchema,
  TopicRefSchema,
])
export type AiAssistRef = z.infer<typeof AiAssistRefSchema>

// ⚠️ O `.strict()` DESTE HELPER é a camada 1 da contenção de escopo a valer.
// Tirá-lo faz um `context` enviado à mão passar a ser ignorado em silêncio em
// vez de rejeitado — e o teste "NÃO aceita um `context` livre" fica vermelho.
// Uma linha, um ponto de falha: é assim que a mutação de prova é barata.
const assistRequestBranch = <T extends AiAssistTarget, R extends z.ZodTypeAny>(target: T, ref: R) =>
  z
    .object({
      target: z.literal(target),
      mode: AiAssistModeSchema,
      instruction: z.string().min(1),
      ref,
    })
    .strict()

/**
 * O corpo do pedido — união discriminada por `target`, para que o `ref` TENHA
 * de casar com o escopo. Sem a discriminação, um `target: 'question.rubricLevel'`
 * com um `topicId` chegaria ao `buildAssistContext` sem `questionId`, isto é,
 * sem nada por onde verificar pertença (camada 6).
 *
 * Os literais saem de `AiAssistTargetSchema.enum` de propósito: retypá-los
 * deixaria a união e o enum divergirem sem o typecheck reparar.
 */
export const AiAssistRequestSchema = z.discriminatedUnion('target', [
  assistRequestBranch(AiAssistTargetSchema.enum['question.canonicalText'], CanonicalTextRefSchema),
  assistRequestBranch(
    AiAssistTargetSchema.enum['question.evaluatorNotes'],
    EvaluatorNotesRefSchema,
  ),
  assistRequestBranch(AiAssistTargetSchema.enum['question.rubricLevel'], RubricLevelRefSchema),
  assistRequestBranch(AiAssistTargetSchema.enum.question, QuestionRefSchema),
  assistRequestBranch(AiAssistTargetSchema.enum.topic, TopicRefSchema),
])
export type AiAssistRequest = z.infer<typeof AiAssistRequestSchema>

/**
 * O guarda de tamanho do assistente — olha para o pedido TODO, não só para a
 * `instruction`.
 *
 * ⚠️ PORQUE EXISTE: `ref.current` é o SEGUNDO texto livre do corpo e entra no
 * prompt VERBATIM. Enquanto o teto mediu só a `instruction`, um `current` de
 * 1,5 MB era aceite com sessão de admin — a camada 5 (custo) limitava pelo
 * campo errado, e o gate de admin limita QUEM abusa, não DE QUÊ.
 *
 * ⚠️ PORQUE NÃO É `.max()` NO SCHEMA: o mesmo motivo do `AI_ASSIST_INSTRUCTION_MAX`
 * — um `.max()` faz o `problemFromZod` devolver 400, e a resposta certa a
 * "grande demais" é 413. E há um segundo motivo, só deste campo: o `current`
 * pode ser um valor LEGÍTIMO ainda por salvar, e recusá-lo como "malformado"
 * ao ABRIR o popup mentiria sobre a causa.
 *
 * Devolve QUAL campo estourou (a rota põe isso no `detail`) ou `null`.
 *
 * O `switch` é exaustivo por construção: um `target` novo no enum sem ramo
 * aqui não compila. Isso é de propósito — o campo esquecido seria exatamente
 * o furo que este helper existe para tapar.
 */
export type AiAssistOversizeField = 'instruction' | 'current'

export function aiAssistOversize(pedido: AiAssistRequest): AiAssistOversizeField | null {
  if (pedido.instruction.length > AI_ASSIST_INSTRUCTION_MAX) return 'instruction'

  // `null` é ausência de valor (modo `create`), não um valor de tamanho zero:
  // não há teto que o recuse.
  const excede = (texto: string | null | undefined, teto: number) =>
    typeof texto === 'string' && texto.length > teto

  switch (pedido.target) {
    case 'question.canonicalText':
      return excede(pedido.ref.current, CANONICAL_TEXT_MAX) ? 'current' : null
    case 'question.evaluatorNotes':
      return excede(pedido.ref.current, EVALUATOR_NOTES_MAX) ? 'current' : null
    case 'question.rubricLevel':
      // O ÚNICO ramo sem verificação, e não por esquecimento: o `current` deste
      // é um `RubricLevelSchema`, que já traz `.max(100)` no `label` e
      // `.max(500)` no `criteria`. O Zod recusa-o antes, com 400. É a assimetria
      // que este arquivo tem de assumir em voz alta, não apagar.
      return null
    case 'question': {
      if (excede(pedido.ref.current?.canonicalText, CANONICAL_TEXT_MAX)) return 'current'
      if (excede(pedido.ref.current?.evaluatorNotes, EVALUATOR_NOTES_MAX)) return 'current'
      // ⚠️ O TERCEIRO CAMPO DESTE RASCUNHO É UM ARRAY, e o `excede` acima não o
      // sabe medir. A rubrica inteira entra no prompt VERBATIM: 2000 níveis
      // válidos (label 100 + criteria 500) são ~1,2 MB e ~300k tokens contra o
      // modelo pago, repetíveis à velocidade do limitador de taxa. E ao
      // contrário do `case 'question.rubricLevel'` — onde o Zod já recusa com
      // 400 — o nível de RASCUNHO não tem `.max()` nenhum: aqui não há segunda
      // linha de defesa, esta é a única.
      //
      // Duas medidas, porque são dois furos distintos: muitos níveis curtos, e
      // poucos níveis enormes.
      const niveis = pedido.ref.current?.rubric ?? []
      if (niveis.length > AI_ASSIST_RUBRIC_LEVELS_MAX) return 'current'
      const caracteres = niveis.reduce((soma, n) => soma + n.label.length + n.criteria.length, 0)
      if (caracteres > AI_ASSIST_RUBRIC_CHARS_MAX) return 'current'
      return null
    }
    case 'topic':
      return excede(pedido.ref.current?.name, TOPIC_NAME_MAX) ? 'current' : null
    // ⚠️ TODOS OS ESCOPOS QUE FICAM TÊM `current`. O `case 'topic.new'` que
    // aqui esteve era o único sem ele — devolvia `null` porque a `instruction`,
    // medida acima do `switch`, era o seu único texto livre. Saiu com o escopo
    // a 2026-09-05 (ver as lápides no docblock de `AiAssistTargetSchema`), tal
    // como o de `question.paraphrases` saíra com a migration 0046.
    default: {
      const naoTratado: never = pedido
      return naoTratado
    }
  }
}

// ---- Propostas ----
//
// A pergunta inteira e o tópico reusam `RubricSchema` (mín. 2 níveis, scores
// únicos) — nenhum schema de rubric novo. Os tetos aparecem aqui porque a rota
// faz CLAMP e só depois valida: cortar antes é clamp, validar antes é recusa.

export const AiAssistQuestionProposalSchema = z
  .object({
    canonicalText: z.string().min(1).max(CANONICAL_TEXT_MAX),
    evaluatorNotes: z.string().min(1).max(EVALUATOR_NOTES_MAX),
    rubric: RubricSchema,
  })
  .strict()

/**
 * Teto de CARDINALIDADE do `questions[]` de uma proposta de tópico — quantas
 * perguntas o corpo do `POST /:id/topics/:topicId/apply` pode trazer numa só
 * escrita.
 *
 * ⚠️ PORQUE NÃO É `.max()` NO SCHEMA: a mesma doutrina de todo este ficheiro —
 * um `.max()` faz o `problemFromZod` devolver 400, e "grande demais" é 413.
 * Constante aqui, `if` explícito na rota.
 *
 * Cada pergunta já é limitada CAMPO A CAMPO
 * (`AiAssistQuestionProposalSchema`: `canonicalText` ≤ `CANONICAL_TEXT_MAX`,
 * `evaluatorNotes` ≤ `EVALUATOR_NOTES_MAX`, `rubric` com `.max()` por nível) —
 * o que faltava travar era a CONTAGEM. A rota reusa este MESMO schema
 * (`AiAssistProposalSchemas.topic`) para validar o corpo do `/apply`, e grava
 * as N perguntas propostas num único `db.batch` (`routes/assessments.ts`): um
 * `questions[]` sem teto é uma escrita sem teto.
 *
 * O número reusa a ordem de grandeza de `ASSIST_SIBLING_QUESTIONS_MAX`
 * (`apps/api/src/lib/assist-context.ts`) — o teto de quantas perguntas irmãs
 * vão ao CONTEXTO do prompt. Ali 20 já foi justificado como "folga larga sobre
 * qualquer tópico plausível deste produto"; o mesmo raciocínio vale aqui.
 *
 * ⚠️ NÃO É um número medido por-tópico contra produção — essa distribuição não
 * foi consultada. O que ANCORA o valor: a `interview_questions` INTEIRA (todos
 * os modelos, todos os tópicos) tinha 40 linhas a 2026-09-03 (ver o cabeçalho
 * de `CANONICAL_TEXT_MAX`); um único tópico com 20 seria metade de tudo o que
 * existe hoje na base. E 20 ≥ 11, o maior caso que `topic-apply.test.ts` já
 * exercita (o que força `chunkRowsForD1` a partir o INSERT em dois statements)
 * — o teto não aperta nenhum caso real conhecido.
 */
export const AI_ASSIST_TOPIC_QUESTIONS_MAX = 20

export const AiAssistTopicProposalSchema = z
  .object({
    name: z.string().min(1).max(TOPIC_NAME_MAX),
    questions: z.array(AiAssistQuestionProposalSchema).min(1),
  })
  .strict()

/**
 * Registo `target → schema da proposta`, SEM a etiqueta.
 *
 * É o que a rota usa para validar a saída do modelo: o LLM responde ao escopo
 * que lhe foi dado e nunca emite `target`. A união etiquetada abaixo é para o
 * cliente, que precisa de discriminar; o servidor já sabe o target.
 */
export const AiAssistProposalSchemas = {
  'question.canonicalText': z.string().min(1).max(CANONICAL_TEXT_MAX),
  'question.evaluatorNotes': z.string().min(1).max(EVALUATOR_NOTES_MAX),
  'question.rubricLevel': RubricLevelSchema,
  question: AiAssistQuestionProposalSchema,
  topic: AiAssistTopicProposalSchema,
} as const satisfies Record<AiAssistTarget, z.ZodTypeAny>

const assistProposalBranch = <T extends AiAssistTarget, P extends z.ZodTypeAny>(
  target: T,
  proposal: P,
) => z.object({ target: z.literal(target), proposal }).strict()

/**
 * A proposta, etiquetada com o `target` que a produziu.
 *
 * A etiqueta é NECESSÁRIA e é ANINHADA: a proposta de `question.canonicalText`
 * é uma `string` nua (§3 do spec), e uma string não pode ser membro de uma
 * união discriminada. Aninhar mantém `proposal` como o valor cru que o
 * `onApply` do spec recebe — `onApply={(next) => replaceLevel(rubric, next)}`
 * continua a receber um `RubricLevel`, não um envelope.
 */
export const AiAssistProposalSchema = z.discriminatedUnion('target', [
  assistProposalBranch(
    AiAssistTargetSchema.enum['question.canonicalText'],
    AiAssistProposalSchemas['question.canonicalText'],
  ),
  assistProposalBranch(
    AiAssistTargetSchema.enum['question.evaluatorNotes'],
    AiAssistProposalSchemas['question.evaluatorNotes'],
  ),
  assistProposalBranch(
    AiAssistTargetSchema.enum['question.rubricLevel'],
    AiAssistProposalSchemas['question.rubricLevel'],
  ),
  assistProposalBranch(AiAssistTargetSchema.enum.question, AiAssistProposalSchemas.question),
  assistProposalBranch(AiAssistTargetSchema.enum.topic, AiAssistProposalSchemas.topic),
])
export type AiAssistProposal = z.infer<typeof AiAssistProposalSchema>

/** O valor cru da proposta de um target — o que o `onApply` do hospedeiro recebe. */
export type AiAssistProposalFor<T extends AiAssistTarget> = Extract<
  AiAssistProposal,
  { target: T }
>['proposal']

/**
 * A recusa estruturada — camada 4 da contenção.
 *
 * O modelo tem uma saída legítima para "isto não é o meu trabalho", e a rota
 * mapeia-a a 422 com a `reason` no `detail`. Sem ela, um pedido desviado
 * voltaria como proposta malformada (500) e o usuário perderia a única
 * informação útil.
 *
 * ⚠️ DE PROPÓSITO SEM `.strict()`, ao contrário dos schemas de ENTRADA: isto
 * lê a saída de um modelo, que pode acrescentar uma chave. Um strict que
 * falhasse aqui deixaria a recusa cair no ramo da proposta e virar 500 —
 * escondendo exatamente a mensagem que existe para ser mostrada.
 */
export const AiAssistRefusalSchema = z.object({
  refused: z.literal(true),
  reason: z.string().min(1).max(300),
})
export type AiAssistRefusal = z.infer<typeof AiAssistRefusalSchema>

// ⚠️ LÁPIDE — AQUI VIVIA O GLOSSÁRIO DE ASR, e saiu a 2026-09-05. Os nomes que
// desapareceram, porque é por eles que se procura: `CreateAsrEntrySchema`,
// `UpdateAsrEntrySchema`, `CreateAsrEntry` e `UpdateAsrEntry` (Story 2.7.1).
// Era uma lista de «ouvi X, entenda Y» por modelo de avaliação, com CRUD próprio
// (`POST`/`PATCH`/`DELETE /:id/asr-glossary`), que viajava no envelope de export.
//
// ⚠️ O LEITOR JÁ TINHA MORRIDO — HÁ SEIS SEMANAS, NÃO HOJE. É tentador olhar
// para o último commit antes desta remoção, não encontrar referência ao
// glossário em `apps/api/src/workflow/` nem em `apps/api/src/llm/`, e concluir
// que ele nunca chegou ao prompt de fusão. ISSO É FALSO, e a história diz o
// contrário: `llm/prompts/fuse-transcripts.ts` tinha 8 menções ao glossário em
// `fae3fdb^` e ZERO em `fae3fdb` (2026-07-25) — incluindo o bloco «Glossário de
// ruído ASR a aplicar:» e a regra «aplique o glossário ASR para corrigir
// nomes/termos». O glossário CORREU, e deixou de correr nessa data.
//
// A leitura do estado ACTUAL não distingue «nunca existiu» de «foi removido»;
// só a história o faz. O que sobrou depois de `fae3fdb` foi uma tabela com CRUD
// e sem leitor — e é isso que sai agora.
//
// A tabela `interview_asr_glossary` sai com a migration 0047.
//
// A ÚNICA coisa que fica é `AssessmentExportSchema.asrGlossary`, mais abaixo, e
// fica `.optional()` por compatibilidade com os arquivos já exportados. Ver lá
// o porquê — e repare que ele NÃO depende de a tabela existir.

// ---- Reorder ----

export const ReorderSchema = z
  .array(
    z.object({
      id: z.string().min(1),
      order: z.number().int().nonnegative(),
    }),
  )
  .min(1)
export type ReorderRequest = z.infer<typeof ReorderSchema>

// ---- Export / Import ----

/**
 * Versioned envelope for assessment export/import.
 * Bumping `version` requires a migration in the import handler.
 *
 * v2 (Epic 2.5 / Story 2.5.1, 2026-05-22): dropped the legacy auto-detect
 * surface — the per-assessment `keywords[]` collection and the
 * minimum-match-count field on the assessment row are gone. v1 exports are
 * no longer importable: the envelope is `.strict()`, so the `version: 1`
 * literal mismatch plus the now-unknown `keywords` key are both rejected.
 */
export const ASSESSMENT_EXPORT_VERSION = 2 as const

export const AssessmentExportSchema = z
  .object({
    version: z.literal(ASSESSMENT_EXPORT_VERSION),
    // Server-generated ISO8601 timestamp of when the export was produced (audit).
    // Optional for backward compatibility with exports/imports predating v1.3.0.
    exportedAt: z.string().datetime().optional(),
    assessment: z.object({
      name: z.string().min(1),
      slug: SlugSchema,
      description: z.string().nullable().optional(),
      enabled: z.boolean().default(true),
    }),
    topics: z.array(
      z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        weight: z.number().positive(),
        order: z.number().int().nonnegative(),
      }),
    ),
    questions: z.array(
      z.object({
        id: z.string().min(1),
        topicId: z.string().min(1),
        // Ao contrário dos schemas de criação/edição acima, o ENVELOPE de
        // import continua a carregar o código: é um arquivo vindo de fora e
        // rejeitá-lo por falta deste campo partia importações que já existem.
        // O valor é aceite e a seguir SOBRESCRITO — `POST /import` renumera o
        // modelo inteiro logo após inserir.
        questionNumber: z.string().min(1),
        canonicalText: z.string().min(1),
        weight: z.number().positive(),
        order: z.number().int().nonnegative(),
        rubric: z.array(RubricLevelSchema).min(2),
        evaluatorNotes: z.string().nullable().optional(),
      }),
    ),
    // ⚠️ ACEITE E IGNORADO — e o `.optional()` é a decisão, não um descuido.
    //
    // As paráfrases saíram do produto a 2026-09-04 com a tabela
    // `interview_question_paraphrases` (migration 0046), e o `POST /import` já
    // não escreve nada a partir desta chave. Mas o envelope é `.strict()`, e
    // TODO arquivo já exportado a carrega (o `GET /:idOrSlug/export` emitia-a
    // sempre, mesmo vazia, até ser removido a 2026-09-02). APAGAR a chave faria
    // o import recusar com 400 cada um desses arquivos — que é precisamente o
    // que o comentário do `questionNumber`, dez linhas acima, diz para não
    // fazer: "é um arquivo vindo de fora e rejeitá-lo … partia importações que
    // já existem".
    //
    // O `.optional()` é o que serve os dois lados: um arquivo NOVO não precisa
    // da chave, um arquivo VELHO não é castigado por a trazer, e em nenhum dos
    // casos ela vai parar à base. A forma interna fica declarada de propósito —
    // um `z.unknown()` aceitaria `paraphrases: "qualquer coisa"`, e a
    // defence-in-depth da Story 2.8 é sobre isso mesmo.
    //
    // Quando não restarem arquivos da era das paráfrases, esta chave sai. Não
    // há data marcada: eles vivem em disco de terceiros.
    paraphrases: z
      .array(
        z.object({
          id: z.string().min(1),
          questionId: z.string().min(1),
          text: z.string().min(1),
        }),
      )
      .optional(),
    // ⚠️ ACEITE E IGNORADA, PELO MESMO MOTIVO DA `paraphrases` LOGO ACIMA — e é
    // literalmente o MESMO caso, pela segunda vez em dois dias, sem assimetria
    // nenhuma entre eles: as duas chaves eram `z.array(…)` obrigatório.
    //
    // O glossário de ASR saiu do produto a 2026-09-05 e o `POST /import` já não
    // escreve nada a partir desta chave. Mas o `GET /:idOrSlug/export` emitia-a
    // sempre, mesmo vazia, até ser removido a 2026-09-02: com a raiz
    // `.strict()`, apagá-la daqui seria 400 em cada arquivo alguma vez
    // exportado, sem excepção.
    //
    // ⚠️ E O MOTIVO NÃO CADUCA COM A TABELA. `interview_asr_glossary` sai com
    // a migration 0047, mas os arquivos de export vivem em disco de terceiros e
    // sobrevivem-lhe: um deles reaparece a ser importado meses depois de a
    // tabela ter deixado de existir, ainda a trazer a chave. É por eles que ela
    // fica declarada, não pelo estado da base.
    //
    // A forma interna fica declarada, como na `paraphrases`: um `z.unknown()`
    // aceitaria `asrGlossary: "qualquer coisa"`, e a defence-in-depth da
    // Story 2.8 é sobre isso mesmo.
    asrGlossary: z
      .array(
        z.object({
          id: z.string().min(1),
          heard: z.string().min(1),
          meaning: z.string().nullable().optional(),
          notes: z.string().nullable().optional(),
        }),
      )
      .optional(),
  })
  // `.strict()` on the root envelope rejects unknown top-level keys on import
  // (Story 2.8 Risks — defence-in-depth against malicious/malformed JSON).
  // This closes the root-level gap; the import handler only reads declared
  // keys regardless. Every key emitted by `GET /:idOrSlug/export` is declared
  // above, so a faithful export round-trips cleanly.
  .strict()
export type AssessmentExport = z.infer<typeof AssessmentExportSchema>

// ---- Problem+JSON (RFC 7807) ----

/**
 * Standard error payload for 4xx/5xx responses (§7.1: `application/problem+json`).
 * Always emit with `Content-Type: application/problem+json`.
 */
export const ProblemDetailsSchema = z.object({
  type: z.string().default('about:blank'),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  errors: z.record(z.array(z.string())).optional(),
})
export type ProblemDetails = z.infer<typeof ProblemDetailsSchema>

// ---- Interview entity read models ----
// Promoted from `apps/web/src/stores/assessment-editor-store.ts` in Story 2.12
// (MNT-003). These are server-shaped read models consumed by the SPA editor;
// they mirror `packages/db/postgres/schema/interviews.ts`. The Create/Update Zod
// schemas above (`CreateTopicSchema`, `CreateInterviewQuestionSchema`, …)
// remain the write-side contract — these interfaces are the read-side shape.

/**
 * InterviewTopic — read model for an interview topic.
 * Shape mirrors `packages/db/postgres/schema/interviews.ts` → `interviewTopics`.
 */
export interface InterviewTopic {
  id: string
  assessmentId: string
  name: string
  weight: number
  order: number
}

/**
 * InterviewQuestion — read model for an interview question.
 * Shape mirrors `packages/db/postgres/schema/interviews.ts` → `interviewQuestions`.
 *
 * Notes:
 *  - `rubric` is the question's 0-10 level matrix (Story 2.6). Persisted as a
 *    JSON column `rubric` in D1 (architecture §5.1). May be absent from the
 *    GET for legacy questions — consumers handle `rubric ?? []`.
 */
export interface InterviewQuestion {
  id: string
  assessmentId: string
  topicId: string
  /**
   * O código canónico ('A1', 'B2') — TEXT no D1, e string desde sempre.
   *
   * Isto esteve declarado `number` durante meses, e não era cosmético: com o
   * tipo a mentir, `a.questionNumber - b.questionNumber` passou o typecheck em
   * dois desempates de ordenação do web. Subtrair strings dá `NaN` em toda a
   * comparação, portanto o desempate nunca desempatou coisa nenhuma — e ninguém
   * viu, porque o compilador tinha dito que estava certo.
   */
  questionNumber: string
  canonicalText: string
  evaluatorNotes: string | null
  weight: number
  order: number
  /** Question rubric level matrix (Story 2.6). Absent for legacy questions. */
  rubric?: RubricLevel[]
}

// ============================================================
// ATS — Customer (cliente) — migration 0022
//
// O dono das vagas. Antes disto o cliente só existia dentro do `title` da vaga
// ("Desenvolvedor .NET Sênior - B3"), o que impedia qualquer consulta por
// cliente sem parsear string.
//
// Criar e editar partilham a MESMA lista de campos (é o mesmo formulário nas
// duas telas), declarada uma vez em `customerWritableShape` — o padrão que
// `CreateProfessionalSchema`/`UpdateProfessionalSchema` já usam mais abaixo, para não
// manter duas listas que divergem com o tempo.
// ============================================================

/** Ciclo de vida do cliente. `inactive` é o soft-delete. */
export const CUSTOMER_STATUSES = ['active', 'inactive'] as const
export const CustomerStatusSchema = z.enum(CUSTOMER_STATUSES)
export type CustomerStatus = z.infer<typeof CustomerStatusSchema>

/**
 * Campos livres do cliente — todos opcionais E anuláveis, pelo mesmo motivo do
 * `jobDescription` da vaga: o formulário envia `campo.trim() || null`, então um
 * campo vazio chega como `null` e não como ausência. Sem `.nullable()`, criar um
 * cliente só com o nome (o caso comum) seria 400.
 *
 * `cnpj` é o TEXTO digitado (com ou sem máscara), com folga de tamanho para
 * '00.000.000/0000-00' (18) — a chave de duplicidade é a versão normalizada,
 * gravada em `customers.cnpj_normalized` pelo handler.
 */
const customerWritableShape = {
  legalName: z.string().max(200).nullable().optional(),
  cnpj: z.string().max(20).nullable().optional(),
  website: z.string().max(200).nullable().optional(),
  industry: z.string().max(100).nullable().optional(),
  contactName: z.string().max(150).nullable().optional(),
  contactEmail: z.string().email().max(200).nullable().optional(),
  contactPhone: z.string().max(30).nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
} as const

/**
 * CNPJ só é validado quando VEM PREENCHIDO: ausente, `null` e '' são estados
 * legítimos (o cadastro de um cliente não espera pelo documento). Quando vem,
 * tem de passar nos dígitos verificadores — a mesma regra que alimenta a coluna
 * espelho com índice único, importada de `./cnpj` para não existir duas vezes.
 *
 * O `| undefined` explícito na propriedade opcional não é decorativo: este
 * repositório liga `exactOptionalPropertyTypes`, então `cnpj?: string | null`
 * NÃO aceita `undefined` e o predicado não casaria com o objeto que o Zod lhe
 * entrega.
 */
const hasValidCnpj = (v: { cnpj?: string | null | undefined }): boolean =>
  v.cnpj === undefined || v.cnpj === null || v.cnpj.trim() === '' || isValidCnpj(v.cnpj)

/**
 * Fábrica, e não uma constante partilhada: o Zod tipa `path` como array MUTÁVEL,
 * e os dois schemas abaixo consomem o mesmo issue — devolver um objeto novo em
 * cada chamada elimina qualquer hipótese de partilha de estado (e um `as const`
 * aqui seria recusado, por tornar `path` readonly).
 */
const cnpjIssue = (): { message: string; path: string[] } => ({
  message: 'CNPJ inválido',
  path: ['cnpj'],
})

/**
 * Criar um cliente. `status` tem default no Zod E na coluna: o default aqui
 * poupa o `?? 'active'` em cada handler, o da coluna protege escritas que não
 * passem por este schema.
 *
 * SEM `slug` (migration 0027). A coluna continua na tabela — o D1 não a deixa
 * sair, ver o topo da 0027 — mas saiu do CONTRATO: quem a preenche é o servidor,
 * derivando-a do `name`. O `.strict()` faz com que um cliente que ainda a envie
 * leve 400 em vez de a ver ignorada em silêncio, que é o que se quer: o campo
 * não tem para onde ir, e aceitá-lo caladamente prometeria um controlo que não
 * existe.
 */
export const CreateCustomerSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    status: CustomerStatusSchema.default('active'),
    ...customerWritableShape,
  })
  .strict()
  .refine(hasValidCnpj, cnpjIssue())
export type CreateCustomer = z.infer<typeof CreateCustomerSchema>

/**
 * PATCH parcial — `id` é imutável. `.strict()` recusa chaves desconhecidas e o
 * primeiro `.refine()` exige ao menos um campo (o padrão de
 * `UpdateOpportunitySchema`). Os campos anuláveis deixam limpar um valor já
 * gravado.
 *
 * NOTA: derivado da MESMA shape do create, não de `CreateCustomerSchema
 * .partial()` — o create termina num `.refine()` (vira `ZodEffects`) e
 * `.partial()` não existe em `ZodEffects`. O resultado é idêntico, e a lista de
 * campos continua a existir uma única vez.
 */
export const UpdateCustomerSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    status: CustomerStatusSchema,
    ...customerWritableShape,
    /**
     * O account manager — o usuário DA GENESIS que cuida da conta (Story 34.2). Só no
     * PATCH: escolhe-se no cliente já criado. A API recusa com 422 quem não for um
     * usuário ativo, `admin` ou `recruiter`, sem cliente. `null` tira-o.
     */
    accountManagerUserId: z.string().min(1).nullable(),
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
  .refine(hasValidCnpj, cnpjIssue())
export type UpdateCustomer = z.infer<typeof UpdateCustomerSchema>

// ============================================================
// ATS — JobProfile (perfil de vaga) — migration 0022, revisto na 0027
//
// O cargo-tipo ("Desenvolvedor C#"), reutilizável entre clientes: nome,
// descrição e estado. NADA de avaliações — a 0022 guardava aqui os dois
// assessments "por omissão" e a 0027 tirou-os a pedido do owner. Quem define
// que entrevista e que prova se aplicam é a VAGA (`CreateOpportunitySchema`,
// mais abaixo), que é onde a verdade sempre esteve.
// ============================================================

export const JOB_PROFILE_STATUSES = ['active', 'inactive'] as const
export const JobProfileStatusSchema = z.enum(JOB_PROFILE_STATUSES)
export type JobProfileStatus = z.infer<typeof JobProfileStatusSchema>

const jobProfileWritableShape = {
  slug: SlugSchema.optional(),
  description: z.string().max(2000).nullable().optional(),
} as const

/** Criar um perfil de vaga. `slug` gerado do `name` quando ausente. */
export const CreateJobProfileSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    status: JobProfileStatusSchema.default('active'),
    ...jobProfileWritableShape,
  })
  .strict()
export type CreateJobProfile = z.infer<typeof CreateJobProfileSchema>

/** PATCH parcial — mesmo contrato do `UpdateCustomerSchema`. */
export const UpdateJobProfileSchema = z
  .object({
    name: z.string().trim().min(1).max(200),
    status: JobProfileStatusSchema,
    ...jobProfileWritableShape,
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateJobProfile = z.infer<typeof UpdateJobProfileSchema>

// ============================================================
// PIPELINE — o processo seletivo configurável (migration 0023)
//
// Um pipeline é um MOLDE: nome, descrição e etapas ordenadas. A vaga escolhe-o
// (W2) e a candidatura percorre-o (W3). Os limites de tamanho são de bom senso
// de interface, não de banco: 20 etapas já é um processo que ninguém consegue
// ler num quadro, e 60 caracteres é o que cabe num cabeçalho de coluna.
// ============================================================

/** Nome de etapa — o que cabe legivelmente num cabeçalho de coluna do Kanban. */
const PipelineStageNameSchema = z.string().trim().min(1).max(60)

/**
 * Uma etapa nomeada. Serve os DOIS caminhos de escrita de etapa — as etapas
 * iniciais no corpo do `POST /api/pipelines`, e o `POST /:id/stages` que
 * acrescenta uma ao fim. Sem id: o servidor gera-o.
 */
export const CreatePipelineStageSchema = z.object({ name: PipelineStageNameSchema }).strict()
export type CreatePipelineStage = z.infer<typeof CreatePipelineStageSchema>

/**
 * Criar um pipeline COM as etapas no mesmo corpo.
 *
 * `stages` é obrigatório e `.min(1)` porque a invariante "um pipeline tem
 * sempre pelo menos uma etapa ativa" tem de valer desde o instante zero — um
 * pipeline sem etapas seria escolhível por uma vaga e não teria onde pôr a
 * primeira candidatura.
 */
export const CreatePipelineSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).nullable().optional(),
    isDefault: z.boolean().optional(),
    stages: z.array(CreatePipelineStageSchema).min(1).max(20),
  })
  .strict()
export type CreatePipeline = z.infer<typeof CreatePipelineSchema>

/**
 * PATCH parcial da identidade do pipeline. As ETAPAS não se editam por aqui —
 * têm endpoints próprios, porque cada mudança de etapa é uma operação com regra
 * própria (a última ativa não se arquiva, a ordem escreve-se em bloco).
 */
export const UpdatePipelineSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    description: z.string().max(2000).nullable(),
    isDefault: z.boolean(),
    /**
     * `false` DESARQUIVA. Sem isto, arquivar era uma porta de sentido único e
     * um clique enganado não tinha volta pela interface. Mesmo par que os
     * catálogos irmãos já têm ("Desativar"/"Reativar" em customers e
     * job_profiles) — só que aqui o estado mora em `archivedAt`, não num enum.
     */
    archived: z.boolean(),
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdatePipeline = z.infer<typeof UpdatePipelineSchema>

/** Renomear uma etapa. O nome é o único campo editável — a posição é do PUT. */
export const UpdatePipelineStageSchema = z.object({ name: PipelineStageNameSchema }).strict()
export type UpdatePipelineStage = z.infer<typeof UpdatePipelineStageSchema>

/**
 * Reordenar — a lista COMPLETA das etapas ativas, na ordem desejada.
 *
 * A lista completa e não pares `{id, position}`: assim o servidor verifica que
 * o conjunto recebido é exatamente o conjunto ativo (nem a mais, nem a menos), e
 * uma etapa esquecida vira 400 em vez de ficar com a posição órfã do estado
 * anterior.
 */
export const ReorderPipelineStagesSchema = z
  .object({ stageIds: z.array(z.string().min(1)).min(1) })
  .strict()
export type ReorderPipelineStages = z.infer<typeof ReorderPipelineStagesSchema>

// ============================================================
// ATS — Opportunity (vaga) — Epic 7 / Story 7.2-7.3 (ADR-012 D1/D3)
// ============================================================

/** Opportunity lifecycle status. `archived` is the soft-delete state. */
export const OPPORTUNITY_STATUSES = ['open', 'closed', 'archived'] as const
export const OpportunityStatusSchema = z.enum(OPPORTUNITY_STATUSES)
export type OpportunityStatus = z.infer<typeof OpportunityStatusSchema>

/**
 * Create an Opportunity. `slug` is auto-generated from `title` (slugify) when
 * absent. `status` defaults to `open` at the handler/DB layer. The two
 * assessment FKs are optional (an opening may not have both set yet); the
 * handler validates each references an assessment of the matching `type`.
 */
export const CreateOpportunitySchema = z.object({
  title: z.string().min(1).max(200),
  slug: SlugSchema.optional(),
  // `.nullable()` pelo mesmo motivo do `notes` de candidatura: o formulário
  // envia `jobDescription.trim() || null`, então uma vaga sem descrição chega
  // com `null`. A coluna é anulável e o handler já grava `?? null`.
  jobDescription: z.string().nullable().optional(),
  status: OpportunityStatusSchema.optional(),
  interviewAssessmentId: z.string().min(1).nullable().optional(),
  practicalAssessmentId: z.string().min(1).nullable().optional(),
  // Cliente e perfil da vaga (migration 0022). Opcionais E anuláveis: as colunas
  // são NULLABLE porque as vagas anteriores à 0022 não têm vínculo, e o
  // formulário envia `null` quando o campo é deixado em branco.
  customerId: z.string().min(1).nullable().optional(),
  jobProfileId: z.string().min(1).nullable().optional(),
  // O processo seletivo (migration 0024, onda W2). Opcional mas NÃO anulável,
  // ao contrário dos dois de cima: se vier ausente o servidor resolve o pipeline
  // padrão, e DESVINCULAR não é um gesto que exista — uma vaga sem processo não
  // tem colunas no Kanban nem para onde mover candidaturas.
  pipelineId: z.string().min(1).optional(),
})
export type CreateOpportunity = z.infer<typeof CreateOpportunitySchema>

/**
 * Partial update — `id` is immutable. Every field optional; `.strict()` rejects
 * unknown keys and `.refine()` requires at least one. Nullable fields let a
 * caller clear a previously-set value (e.g. unset an assessment).
 */
export const UpdateOpportunitySchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    slug: SlugSchema.optional(),
    jobDescription: z.string().nullable().optional(),
    status: OpportunityStatusSchema.optional(),
    interviewAssessmentId: z.string().min(1).nullable().optional(),
    practicalAssessmentId: z.string().min(1).nullable().optional(),
    // `null` explícito desvincula a vaga do cliente/perfil (mesma semântica que
    // os dois assessment ids já tinham).
    customerId: z.string().min(1).nullable().optional(),
    jobProfileId: z.string().min(1).nullable().optional(),
    // Não anulável — ver `CreateOpportunitySchema`. Trocar o processo é
    // recusado com 409 enquanto houver candidaturas em andamento; a regra vive
    // no handler, porque depende de uma contagem que o zod não pode fazer.
    pipelineId: z.string().min(1).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateOpportunity = z.infer<typeof UpdateOpportunitySchema>

// ============================================================
// ATS — Application (Professional↔Opportunity) — Story 7.5a (ADR-012 D1/D2)
// ============================================================

// O enum de ETAPAS morreu na onda W5 (migration 0030), com a coluna que ele
// descrevia. As etapas passaram a ser linhas de `pipeline_stages`, escolhidas
// por processo e por vaga: quatro valores fixos aqui seriam quatro mentiras num
// processo com outros nomes. A etapa de uma candidatura é o último evento de
// `application_events`, e o ponteiro `current_stage_event_id` diz qual é.

export const APPLICATION_STATUSES = ['active', 'rejected', 'hired', 'withdrawn'] as const
export const ApplicationStatusSchema = z.enum(APPLICATION_STATUSES)
export type ApplicationStatus = z.infer<typeof ApplicationStatusSchema>

/**
 * O ÚNICO desfecho que abre a alocação — "a empresa ficou com esta pessoa".
 *
 * ⚠️ Não existe status `approved` no vocabulário: os quatro são os de cima, e o
 * que significa aprovação é `hired` ("Contratada" na interface). A constante
 * nomeia a REGRA, não o valor, e é por isso que se chama assim: quem a lê num
 * `if` percebe que aquele ramo é sobre poder alocar, e não sobre uma palavra.
 *
 * ⚠️ VIVE AQUI, no pacote partilhado, e não do lado do browser — de propósito.
 * O portão da alocação tem DUAS metades que têm de concordar: a interface, que
 * decide o que oferece (o botão da faixa, a lista do diálogo), e o servidor, que
 * decide o que aceita (`POST /api/allocations`). Se as duas escolhessem o
 * literal cada uma por si, o dia em que uma mudasse deixaria a outra a oferecer
 * o que a outra recusa — e um produto que sugere e depois se recusa a si próprio
 * lê-se como avaria, não como regra.
 */
export const ALLOCATABLE_APPLICATION_STATUS: ApplicationStatus = 'hired'

// ============================================================
// O ID DA PESSOA NO FIO — `professionalId`, E SÓ ELE.
//
// A plataforma renomeou a pessoa de "candidato" para "profissional". Durante a
// transição a API aceitou os DOIS nomes (`candidateId` ⇄ `professionalId`),
// porque o `apps/web` e o `apps/api` sobem em workflows SEPARADOS e não
// atómicos: sem a ponte havia uma janela em que o SPA novo falava com a API
// velha (ou o contrário) e criar candidatura ou alocação era 400 para toda a
// gente.
//
// A PONTE FOI LEVANTADA. O `apps/web` já só envia `professionalId`, portanto o
// nome antigo deixou de ser aceite: um corpo que traga só `candidateId` é 400
// por falta de `professionalId`, e não uma aceitação silenciosa com um id
// adivinhado.
//
// ⚠️ ISTO ERA SOBRE O FIO, E A BASE JÁ ACOMPANHOU. Enquanto este bloco falava, o
// nome FÍSICO da coluna ainda era `candidate_id`; a migration 0039 renomeou-a
// para `professional_id` (e a tabela para `professionals`). Os dois lados dizem
// hoje a mesma palavra — o que sobrevive de "candidato" é o PAPEL na candidatura,
// não a pessoa.
// ============================================================

/** A frase do 400 quando o id da pessoa não veio (ou veio vazio). */
export const ID_DA_PESSOA_AUSENTE = 'Informe `professionalId`.'

/**
 * O campo do id da pessoa, para espalhar (`...`) na forma de um schema.
 *
 * Vive num sítio só porque a frase da recusa tem de ser a mesma nos dois
 * schemas que o usam: duas cópias divergiriam, e a divergência apareceria como
 * "criar candidatura explica o campo e criar alocação não".
 */
export const idDaPessoaShape = {
  professionalId: z
    .string({ required_error: ID_DA_PESSOA_AUSENTE, invalid_type_error: ID_DA_PESSOA_AUSENTE })
    .min(1, ID_DA_PESSOA_AUSENTE),
}

/**
 * Create an Application. `professionalId` + `opportunityId` are required and
 * immutable (the junction key). `stage`/`status` default at the DB layer.
 */
export const CreateApplicationSchema = z.object({
  ...idDaPessoaShape,
  opportunityId: z.string().min(1),
  status: ApplicationStatusSchema.optional(),
  // `.nullable()` porque o formulário envia `notes: notes.trim() || null` — um
  // campo de observações vazio vira `null`, não ausência. Sem isto, criar
  // candidatura sem observações era 400 (o caso comum). O handler já fazia
  // `d.notes ?? null` e o UpdateApplicationSchema abaixo já aceitava null;
  // era este schema que divergia das outras duas camadas.
  notes: z.string().nullable().optional(),
})
export type CreateApplication = z.infer<typeof CreateApplicationSchema>

/**
 * Partial update — the junction key (professional/opportunity) is immutable.
 *
 * `primary{Interview,Practical}EvaluationId` fixam qual avaliação daquele tipo
 * representa a candidatura (migration 0019). Semântica dos três estados:
 *   • ausente do body → não mexe no ponteiro;
 *   • string          → fixa aquela avaliação como ativa (o endpoint valida que
 *                       ela existe, é DESTA candidatura e é do tipo certo — 400
 *                       caso contrário);
 *   • null            → limpa a escolha manual, voltando ao fallback automático
 *                       "a mais recente daquele tipo".
 *
 * ⚠️ `stage` e `status` SAÍRAM daqui na onda W3 (migration 0025). As duas coisas
 * passam a mover-se só por `POST /api/applications/:id/events`, que é o único
 * caminho que grava história com data, hora e autor. O `.strict()` faz com que
 * enviá-las aqui devolva 400 — recusar é melhor do que aceitar e ignorar em
 * silêncio, que deixaria quem chamou a pensar que moveu.
 *
 * `archived` ENTRA aqui, e não naquele endpoint, precisamente pela mesma régua:
 * arquivar não é um facto sobre a pessoa nem sobre o processo dela — é
 * arrumação de lista. Não tem história para gravar, tem estado para mudar.
 */
export const UpdateApplicationSchema = z
  .object({
    notes: z.string().nullable().optional(),
    primaryInterviewEvaluationId: z.string().min(1).nullable().optional(),
    primaryPracticalEvaluationId: z.string().min(1).nullable().optional(),
    /**
     * `false` DESARQUIVA — a volta que o `DELETE /api/applications/:id` não
     * tinha. Mesmo campo, mesmo nome e mesma semântica de
     * `UpdatePipelineSchema.archived`: o inverso de um estado é o mesmo campo
     * com o outro valor, nunca um endpoint novo.
     */
    archived: z.boolean().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateApplication = z.infer<typeof UpdateApplicationSchema>

/**
 * Um evento na história da candidatura (migration 0025, onda W3).
 *
 * `type` discrimina o que o corpo tem de trazer, e o `.refine()` impõe-o: um
 * evento de etapa sem `stageId` — ou de status sem `status` — não descreve nada,
 * e aceitá-lo encheria a timeline de linhas mudas.
 *
 * `occurredAt` é opcional: por omissão é agora. Existe para registar um
 * movimento que aconteceu antes (uma entrevista de ontem lançada hoje) sem
 * mentir sobre quando foi.
 */
export const ApplicationEventTypeSchema = z.enum(['stage', 'status'])
export type ApplicationEventType = z.infer<typeof ApplicationEventTypeSchema>

export const CreateApplicationEventSchema = z
  .object({
    type: ApplicationEventTypeSchema,
    stageId: z.string().min(1).optional(),
    status: ApplicationStatusSchema.optional(),
    note: z.string().max(2000).nullable().optional(),
    occurredAt: z.string().datetime().optional(),
  })
  .strict()
  .refine(
    (v) => (v.type === 'stage' ? Boolean(v.stageId) : Boolean(v.status)),
    'Um evento de etapa precisa de `stageId`; um de status precisa de `status`.',
  )
export type CreateApplicationEvent = z.infer<typeof CreateApplicationEventSchema>

// ============================================================
// ATS — Professional (tabela `professionals` desde a migration 0039)
//
// Criar e editar usam os MESMOS campos: a tela de cadastro (/professionals/new) e
// a de edição são o mesmo formulário. Para não manter duas listas de 17 campos
// que divergem com o tempo, o bloco de perfil é declarado UMA vez e os dois
// schemas derivam dele.
//
// Validação deliberadamente LENIENTE nos campos de contato livres, para não
// recusar formatos reais:
//   * phone: máx. 30 caracteres, SEM regex de formato ('(11) 99999-9999',
//     '+5511…', …). A comparação de duplicidade é feita sobre o valor
//     NORMALIZADO (ver @caliber/shared/professional-identity), não sobre o texto.
//   * linkedinUrl: máx. 150, NÃO `.url()` — 'linkedin.com/in/x' não tem esquema
//     e falharia; handles curtos também são aceitos.
//
// `email` É editável (decisão do owner, 2026-07-27) — revoga a regra da Story
// 11.1 que o tratava como identidade imutável. A trava de duplicidade protege a
// alteração tanto quanto protegia a criação.
// ============================================================

/** Tri-state de modalidade/status: true | false | null (desconhecido). */
const NullableBool = z.boolean().nullable()
/** Salário em reais inteiros ou aviso prévio em dias: inteiro não-negativo ou null. */
const NullableNonNegInt = z.number().int().nonnegative().nullable()

export const CONTRACT_TYPES = ['clt', 'pj', 'both'] as const
export const ContractTypeSchema = z.enum(CONTRACT_TYPES)
export type ContractType = z.infer<typeof ContractTypeSchema>

/**
 * A senioridade do profissional (Story 31.5, migration 0056): Júnior, Pleno, Sênior,
 * Especialista — o vocabulário das vagas dos clientes, decisão do owner no #467. Uma
 * coluna TEXT sem CHECK, como o contrato: o enum vive aqui.
 */
export const PROFESSIONAL_SENIORITIES = ['junior', 'pleno', 'senior', 'especialista'] as const
export const ProfessionalSenioritySchema = z.enum(PROFESSIONAL_SENIORITIES)
export type ProfessionalSeniority = z.infer<typeof ProfessionalSenioritySchema>

/** Os 18 campos de perfil, partilhados por criar e editar. */
const professionalProfileShape = {
  // Contato.
  phone: z.string().max(30).nullable().optional(),
  linkedinUrl: z.string().max(150).nullable().optional(),
  // Localização.
  city: z.string().max(100).nullable().optional(),
  state: z.string().max(100).nullable().optional(),
  country: z.string().max(100).nullable().optional(),
  // Remuneração — reais inteiros (sem centavos). Não-negativos.
  expectedSalaryClt: NullableNonNegInt.optional(),
  expectedSalaryPj: NullableNonNegInt.optional(),
  currentSalary: NullableNonNegInt.optional(),
  // Modalidade de trabalho + status de emprego (tri-state).
  openToRemote: NullableBool.optional(),
  openToHybrid: NullableBool.optional(),
  openToOnsite: NullableBool.optional(),
  isCurrentlyEmployed: NullableBool.optional(),
  // Contexto do emprego atual.
  currentRole: z.string().max(150).nullable().optional(),
  currentCompany: z.string().max(150).nullable().optional(),
  noticePeriodDays: NullableNonNegInt.optional(),
  willingToRelocate: NullableBool.optional(),
  preferredContractType: ContractTypeSchema.nullable().optional(),
  seniority: ProfessionalSenioritySchema.nullable().optional(),
} as const

/** Identidade — comum aos dois schemas, com `name` variando em obrigatoriedade. */
const professionalIdentityShape = {
  email: z.string().email().nullable().optional(),
  /**
   * E-MAIL GENESIS (migration 0053, Story 24.9) — o e-mail corporativo do
   * profissional alocado, e o ÚNICO que o cliente vai ver no portal (24.10, D11).
   * Opcional; o que se valida é o FORMATO. Sem regra de domínio (a story não a
   * pede) e fora da trava de duplicidade: a pessoa continua identificada pelo
   * `email` pessoal, que é o que tem índice único.
   *
   * As quatro colunas da FOTO (`photo*`) NÃO entram aqui de propósito: são do
   * servidor, escritas só por `PUT/DELETE /api/professionals/:id/photo`. O
   * `.strict()` dos dois schemas recusa-as com 400.
   */
  genesisEmail: z.string().email().max(254).nullable().optional(),
  githubUsername: z.string().min(1).max(100).nullable().optional(),
  notes: z.string().nullable().optional(),
} as const

export const CreateProfessionalSchema = z
  .object({
    name: z.string().min(1).max(200),
    ...professionalIdentityShape,
    ...professionalProfileShape,
  })
  .strict()
export type CreateProfessional = z.infer<typeof CreateProfessionalSchema>

/**
 * PATCH parcial: só os campos PRESENTES no corpo são escritos. Um campo
 * omitido preserva o que está gravado (é assim que o tri-state `null` =
 * "desconhecido" sobrevive); um `null` explícito limpa o valor.
 *
 * `archived` ENTRA aqui e NÃO em `CreateProfessionalSchema`, pela mesma régua de
 * `UpdateApplicationSchema`: arquivar não é um facto sobre a pessoa nem um
 * campo do cadastro — é arrumação de lista. Ninguém nasce arquivado.
 */
export const UpdateProfessionalSchema = z
  .object({
    name: z.string().min(1).max(200).optional(),
    ...professionalIdentityShape,
    ...professionalProfileShape,
    /**
     * `false` DESARQUIVA. Mesmo campo, mesmo nome e mesma semântica de
     * `UpdateApplicationSchema.archived` e `UpdatePipelineSchema.archived`: o
     * inverso de um estado é o mesmo campo com o outro valor, nunca um endpoint
     * novo.
     *
     * NÃO substitui o `DELETE /api/professionals/:id`, que continua a ser a
     * exclusão FÍSICA guardada por 409. Os dois respondem a perguntas
     * diferentes: excluir é para o cadastro sem vínculo nenhum (senão o email
     * fica preso num índice único sem história para preservar); arquivar é para
     * quem tem vínculos e por isso não pode ser apagado.
     */
    archived: z.boolean().optional(),
  })
  .strict()
export type UpdateProfessional = z.infer<typeof UpdateProfessionalSchema>

// ============================================================
// ATS — Resume (generated CV) — Stories 7.10-7.11 (ADR-012 D4)
// ============================================================

/** Trigger LLM generation of a CV for an application. */
export const GenerateResumeSchema = z.object({
  applicationId: z.string().min(1),
})
export type GenerateResume = z.infer<typeof GenerateResumeSchema>

/** Edit a generated CV (title and/or content). */
export const UpdateResumeSchema = z
  .object({
    title: z.string().min(1).max(200).optional(),
    content: z.string().min(1).optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateResume = z.infer<typeof UpdateResumeSchema>

// ============================================================
// ATS — Evaluation writer (POST /api/evaluations) — Epic 7 (ADR-012 §D2)
// ============================================================

/** Which assessment of the Opportunity an evaluation runs against. */
export const EVALUATION_TYPES = ['interview', 'practical'] as const
export const EvaluationTypeSchema = z.enum(EVALUATION_TYPES)
export type EvaluationType = z.infer<typeof EvaluationTypeSchema>

/**
 * Create an Evaluation for an Application (the keystone writer — exercises the
 * NOT NULL `evaluations.application_id` constraint, migration 0010).
 *
 * The client supplies ONLY the `applicationId`, the `type`, an optional LLM
 * override and the per-type inputs. `professionalId` and `assessmentId` are NEVER
 * accepted from the client — they are DERIVED server-side from the application
 * and its opportunity (Story 7.5b — avoids identity drift).
 *
 * The required input depends on `type` (validated in the handler, which emits a
 * 422 — Zod only guards the field shapes here):
 *   - `interview`: at least one of `videoR2Key`/`userTranscriptR2Key`.
 *   - `practical`: `repoUrl` (the git URL the runner clones, builds and tests —
 *     persisted to `evaluations.repo_url`; consumed by PracticalGradingWorkflow,
 *     Story 6.0 — wires the PRACTICAL_GRADING trigger, DEBT-EPIC5-TRIGGER-001).
 */
export const CreateEvaluationSchema = z.object({
  applicationId: z.string().min(1),
  type: EvaluationTypeSchema,
  /**
   * Optional provider override — defaults to the worker's configured provider.
   *
   * Fechado a `'openai'` desde 2026-09-24. Era `z.string().min(1)` aberta, e
   * qualquer string chegava ao `createLLM`: hoje isso rebentaria lá dentro com
   * um 500 em vez de um 400. Um valor que o sistema não pode servir recusa-se
   * na fronteira, com o nome do campo, e não no meio de uma avaliação.
   */
  llmProvider: z.literal('openai').optional(),
  /** Practical input — git URL the runner clones (required when type=practical). */
  repoUrl: z.string().url().optional(),
  videoR2Key: z.string().min(1).optional(),
  userTranscriptR2Key: z.string().min(1).optional(),
})
export type CreateEvaluation = z.infer<typeof CreateEvaluationSchema>

/**
 * Corpo (OPCIONAL) de `POST /api/evaluations/:id/reprocess` — Story 20.1.
 *
 * O reprocessamento existe desde 2026-07-28 sem corpo nenhum, e continua a
 * funcionar assim: um pedido sem corpo é `{}` e produz exactamente o resultado
 * de sempre. O que este schema acrescenta é UM campo opcional — a instrução em
 * texto livre que dirige aquela reavaliação ("dê mais peso à profundidade
 * técnica nas respostas sobre concorrência").
 *
 * `.strict()` porque é a única defesa contra um corpo que traz mais do que o
 * contrato promete: sem ele, um `{ instruction, llmModel }` passaria e o campo
 * a mais seria descartado em silêncio, dando ao cliente a impressão de que foi
 * lido.
 *
 * ⚠️ SEM `.max()` no campo, e é deliberado — mesma decisão de
 * `AI_ASSIST_INSTRUCTION_MAX` acima (`:276-283`): um `.max()` faria
 * `problemFromZod` devolver 400, e "o teu pedido é grande demais" é 413, não
 * corpo malformado. O teto é o MESMO 2000 do assistente de IA, aplicado por um
 * `if` explícito na rota, ANTES do primeiro `SELECT` — é o mesmo risco (texto
 * livre do cliente a entrar num prompt) e por isso é a mesma resposta, e não
 * uma constante nova.
 */
export const ReprocessEvaluationRequestSchema = z
  .object({
    instruction: z.string().optional(),
  })
  .strict()
export type ReprocessEvaluationRequest = z.infer<typeof ReprocessEvaluationRequestSchema>

// ---- Grading config (Story 13.3 — Epic 13) ----

/**
 * Config de avaliação por prova prática, gravada na coluna `assessments.grading_config`
 * (JSON). Nasce COMPLETO (forward-compat): só `subjectiveInstructions` é CONSUMIDO
 * nesta story (anexado ao prompt subjetivo do LLM); os demais campos são params
 * determinísticos do runner, armazenados aqui e consumidos pela Story 13.4 (ficam
 * inertes até lá — armazenar-sem-consumir é intencional, não defeito).
 *
 * TODOS os campos são opcionais → `{}` é válido e equivale a "config nula" para os
 * consumidores. `.strict()` rejeita chaves desconhecidas (não-editáveis como gabarito
 * e prompts de detecção de IA NÃO entram aqui — ficam definidos em código, read-only).
 *
 * VALIDAÇÃO CRUZADA `coveragePartialThreshold <= coveragePassThreshold` NÃO vive no
 * schema (Zod valida cada campo de forma independente) — é aplicada pelo endpoint
 * PATCH, pois um deles pode estar ausente no replace parcial.
 */
export const GradingConfigSchema = z
  .object({
    // 13.3 — dimensão subjetiva (LLM, API-side). Único campo consumido nesta story.
    subjectiveInstructions: z.string().max(4000).optional(),
    // 13.4 — params do runner (determinístico; armazenados aqui, consumidos na 13.4).
    tolerance: z.number().gt(0).lte(0.5).optional(),
    coveragePassThreshold: z.number().min(0).max(100).optional(),
    coveragePartialThreshold: z.number().min(0).max(100).optional(),
    checkWeights: z.record(z.string(), z.number().gt(0)).optional(),
  })
  .strict()

export type GradingConfig = z.infer<typeof GradingConfigSchema>

// ============================================================
// BEHAVIORAL — avaliação comportamental por COMPETÊNCIAS (Epic 17)
//
// Gravada em `assessments.behavioral_config` (rubrica, migration 0019) e em
// `evaluations.behavioral_result` (resultado). As duas colunas são TEXT em modo
// JSON, sem schema imposto pelo D1 — a fronteira de validação é o zod, aqui.
//
// FONTE DE VERDADE DO CONTEÚDO: `docs/metodologia-comportamental/rubrica-v0.md`
// — as 4 competências da §1, as 16 âncoras da §2 e as regras de pontuação da
// §4. O desenho dos dois contratos é o de `04-plano-de-alteracao.html` §02.
//
// O QUE MUDOU, E POR QUÊ A QUEBRA É INTENCIONAL. Até o Epic 17 isto era uma
// nota única de 0 a 10, saída de uma escala gráfica holística, com evidências
// avulsas e um `recommendationImpact` que traduzia o número em instrução. Agora
// são QUATRO competências, cada uma com um nível de 1 a 4 ancorado em
// comportamento observável (BARS), a sua razão e as suas citações literais com
// timestamp — mesma gramática de leitura da avaliação técnica, sem número
// único. O `recommendationImpact` foi removido SEM substituto automático (D2 do
// epic): nada traduz nível em recomendação.
//
// Resultado gravado no formato antigo NÃO é convertido (D6): `.safeParse` falha,
// `parseBehavioral()` em `routes/evaluations.ts` devolve `null`, a avaliação
// aparece como não avaliada e o reprocessamento refaz. Isso é comportamento
// desejado, não degradação — e é o que o teste de contrato prende.
// ============================================================

/**
 * Um dos quatro níveis BARS de uma competência: o número e a âncora que o
 * descreve.
 *
 * O que separa um nível do seguinte é sempre um comportamento específico
 * presente na transcrição, nunca a intensidade de um adjetivo (rubrica §2).
 */
export const CompetencyLevelSchema = z.object({
  level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  description: z.string().min(1).max(800), // a âncora BARS deste nível
})
export type CompetencyLevel = z.infer<typeof CompetencyLevelSchema>

/**
 * Uma competência da rubrica: o que se avalia, e as quatro âncoras que decidem
 * o nível.
 *
 * DECISÃO (AC-1 da story 17.2) — o `.refine()` de `levels` compara os quatro
 * níveis **por POSIÇÃO** (`levels[i].level === i + 1`), não por conjunto de
 * valores. `.length(4)` sozinha aceitaria `[1, 2, 2, 3]`; um `Set` de tamanho 4
 * aceitaria `[4, 3, 2, 1]`. A comparação posicional barra as duas de uma vez e
 * ainda garante que quem renderiza a rubrica — o editor, o prompt, o relatório
 * — não precisa ordenar nada: a ordem do array É 1→4. É estritamente mais forte
 * que a comparação por valor, e o custo é zero.
 */
export const CompetencySchema = z
  .object({
    // Estável: é o `competencyId` que o resultado referencia e que 17.3/17.4/
    // 17.5 resolvem para rótulo. Renomear um id faz o rótulo virar o id cru na
    // tela do cliente, sem erro nenhum — por isso há teste sobre os quatro.
    id: z.string().min(1).max(60),
    name: z.string().min(1).max(120),
    definition: z.string().min(1).max(600),
    levels: z
      .array(CompetencyLevelSchema)
      .length(4)
      .refine((levels) => levels.every((l, i) => l.level === i + 1), {
        message: 'levels deve trazer os níveis 1, 2, 3 e 4 — uma vez cada, nessa ordem',
      }),
    evidenceHint: z.string().min(1).max(400), // o que procurar na transcrição

    /**
     * A pergunta do bloco dedicado que produz a evidência desta competência
     * (rubrica §3). NÃO é uma `interview_questions`: não tem peso nem rubrica de
     * nota e não entra no score técnico — pôr as comportamentais lá contaminaria
     * exatamente a nota que este método veio separar.
     *
     * ⚠️ A AUSÊNCIA SIGNIFICA ALGO: "esta competência não tem pergunta
     * dedicada". É o caso de `comunicacao`, que a rubrica §2 descreve como "a
     * única competência que não depende de episódio narrado" — lê-se na forma de
     * falar ao longo de toda a entrevista. Por isso o campo é OPCIONAL e o
     * `.min(1)` proíbe a string vazia: com ela haveria um terceiro estado
     * indistinguível de "ainda não escrevi".
     *
     * OPCIONAL, e não obrigatório com default, por compatibilidade: o schema é
     * `.strict()` e `resolveBehavioralConfig` cai no PADRÃO quando o `safeParse`
     * falha — uma rubrica CUSTOMIZADA gravada antes deste campo existir voltaria
     * silenciosamente ao padrão, que é perda de dados sem um único erro.
     */
    question: z.string().min(1).max(400).optional(),

    /**
     * Os aprofundamentos obrigatórios da pergunta acima (rubrica §3).
     *
     * Não são decoração conversacional: a §4.3 faz o TETO do nível depender
     * deles — sem o pedido de individualização a competência fica "não
     * observada" (`sem-follow-up-de-acao`), e sem o pedido de desfecho o teto
     * para no nível 3 (`sem-follow-up-de-resultado`). Ausente = a competência
     * não define aprofundamentos próprios.
     */
    followUps: z.array(z.string().min(1).max(200)).max(6).optional(),
  })
  .strict()
export type Competency = z.infer<typeof CompetencySchema>

/**
 * Rubrica comportamental completa e EDITÁVEL de um assessment de entrevista.
 *
 * `.strict()` rejeita chaves desconhecidas (400 em vez de silenciosamente
 * descartar) — inclusive as do formato antigo (`dimensions`, `scoreAnchors`,
 * `redFlags`, `greenFlags`, `modulationRules`), que deixaram de existir e NÃO
 * ficaram como campos opcionais "para compatibilidade".
 *
 * `enabled: false` desliga a avaliação comportamental daquele assessment sem
 * perder o conteúdo já escrito — preferível a apagar a config.
 */
export const BehavioralConfigSchema = z
  .object({
    enabled: z.boolean(),
    title: z.string().min(1).max(200),
    objective: z.string().min(1).max(2000),
    competencies: z.array(CompetencySchema).min(3).max(6),

    /**
     * Os aprofundamentos que se colam às perguntas TÉCNICAS em voo (rubrica §3,
     * "Alavanca em voo") — não pertencem a nenhuma competência, e é por isso que
     * vivem aqui e não dentro de {@link CompetencySchema}.
     *
     * Gatilho: a pessoa disse "a gente" sobre algo que aconteceu. Não pedem
     * tempo novo nem bloco novo. São a pré-condição de `autonomia`,
     * `colaboracao` e `adaptacao` ao mesmo tempo: sem ação individual não há
     * episódio atribuível a ninguém, e o teto de §4.3 cai para "não observada".
     *
     * OPCIONAL pela mesma razão que {@link CompetencySchema}.`question`: uma
     * config gravada antes deste campo tem de continuar a parsear.
     */
    inFlightFollowUps: z.array(z.string().min(1).max(200)).max(6).optional(),
  })
  .strict()
export type BehavioralConfig = z.infer<typeof BehavioralConfigSchema>

/**
 * A fase STAR de onde a citação foi tirada — Situação, Tarefa, Ação, Resultado.
 *
 * Não é decoração: a rubrica §4.3 diz que a **Ação individual** pesa mais que o
 * resto do episódio, porque é a parte que prevê comportamento futuro. Marcar a
 * fase é o que deixa conferir, depois, se o nível se apoiou numa Ação ou apenas
 * no contexto que a rodeava.
 */
export const StarPhaseSchema = z.enum(['S', 'T', 'A', 'R'])
export type StarPhase = z.infer<typeof StarPhaseSchema>

/** Uma citação literal da transcrição, com marca de tempo e fase STAR. */
export const BehavioralCitationSchema = z.object({
  text: z.string().min(1).max(600), // literal — citação exata da transcrição
  time: z.string().min(1).max(20), // timestamp, mesmo formato usado no resto do relatório
  star: StarPhaseSchema,
})
export type BehavioralCitation = z.infer<typeof BehavioralCitationSchema>

/**
 * A leitura de UMA competência nesta entrevista.
 *
 * `level: null` significa **não observada** — nunca nível 1. Confundir ausência
 * de evidência com evidência negativa é o erro mais caro deste desenho
 * (rubrica §4.1, D3 do epic).
 *
 * O `rationale` responde **"por que a nota parou aqui"** — e NÃO apenas "por
 * que falta nota". A diferença não é estilística: são QUATRO motivos (rubrica
 * §4.6), e um deles acompanha um nível preenchido.
 *
 * | Motivo | Quando | `level` | Frase canônica (§4.6) |
 * |---|---|---|---|
 * | `pergunta-nao-feita` | A pergunta daquela competência não foi feita | `null` | "Não observada — a pergunta não foi feita nesta entrevista" |
 * | `sem-follow-up-de-acao` | Houve episódio, mas ninguém pediu para individualizar a ação | `null` | "Não observada — o episódio existe, mas não se sabe o que esta pessoa fez nele" |
 * | `sem-episodio` | A pergunta foi feita, a pessoa desviou e não houve insistência | `null` | "Não observada — a pergunta foi feita, mas a conversa não chegou a um caso concreto" |
 * | `sem-follow-up-de-resultado` | Episódio com ação individual, e ninguém perguntou como souberam que funcionou | **3** | "Nível 3 — o nível 4 exigia o desfecho, e o desfecho não chegou a ser perguntado" |
 *
 * Três motivos acompanham `level: null`; o quarto acompanha `level: 3` e
 * explica de onde veio o teto. Ler o `rationale` como "motivo da ausência" é o
 * erro que faz o quarto desaparecer — e ler o contrato como se lhe faltasse um
 * campo é o erro simétrico: NÃO inventar `questionAsked: boolean` nem
 * `reason: enum`, que não estão no contrato aprovado (Article IV — No
 * Invention). O motivo é exigência de REDAÇÃO sobre um campo que já existe.
 *
 * A frase canônica é o **começo** do `rationale`, não o seu todo: depois dela
 * vem a justificativa daquela leitura. Um `rationale` que seja apenas a frase
 * canônica é válido para o Zod e pobre para quem lê — a exigência de conteúdo é
 * do prompt (17.3), não do schema. Ver {@link BEHAVIORAL_MOTIVE_SENTENCES}.
 *
 * `citations` pode ser vazio quando `level` é `null` (não houve episódio — não
 * há o que citar). Quando `level` NÃO é `null`, recomenda-se ao menos uma
 * citação, mas isso é regra de conteúdo e vive no prompt: o Zod garante forma,
 * não qualidade da evidência.
 */
export const AssessedCompetencySchema = z
  .object({
    competencyId: z.string().min(1).max(60), // referencia Competency.id da config aplicada
    level: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.null()]),
    rationale: z.string().min(1).max(800),
    citations: z.array(BehavioralCitationSchema).max(8),
  })
  .strict()
export type AssessedCompetency = z.infer<typeof AssessedCompetencySchema>

/**
 * Confiança da leitura comportamental.
 *
 * ⚠️ DESVIO CONSCIENTE DA RUBRICA §4.5 — DÉBITO CONHECIDO, NÃO DESENHO FINAL.
 * A rubrica deriva a confiança **por competência**: cada competência é lida três
 * vezes de forma independente, e a dispersão entre as três leituras vira o grau
 * de confiança. O contrato aprovado em `04-plano-de-alteracao.html` §02 tem
 * **um** campo `confidence`, no nível do resultado inteiro, e esta story mantém
 * o contrato do plano em vez de inventar campo (Article IV).
 *
 * Consequência: a confiança publicada é a do **pior caso** entre as competências
 * avaliadas. É conservador e **perde informação** — uma competência lida três
 * vezes igual pode ser reportada como "baixa" por causa de outra. Escrito aqui
 * para não ser redescoberto como bug quando o relatório mostrar "confiança
 * baixa" ao lado de quatro leituras unânimes.
 *
 * Caminho de saída, para o dia em que o owner decidir pagar o débito:
 * acrescentar `confidence` a {@link AssessedCompetencySchema} e derivar o campo
 * do resultado a partir delas (ou removê-lo).
 */
export const BehavioralConfidenceSchema = z.enum(['alta', 'media', 'baixa'])
export type BehavioralConfidence = z.infer<typeof BehavioralConfidenceSchema>

/**
 * RESULTADO da avaliação comportamental de UMA execução, gravado em
 * `evaluations.behavioral_result`.
 *
 * Sem nota agregada das quatro competências, de propósito: elas não se somam, e
 * somá-las devolveria a falsa precisão que este método veio eliminar
 * (rubrica §4.6).
 */
export const BehavioralResultSchema = z
  .object({
    competencies: z.array(AssessedCompetencySchema).min(1),
    summary: z.string().min(1).max(2000),
    confidence: BehavioralConfidenceSchema,
  })
  .strict()
export type BehavioralResult = z.infer<typeof BehavioralResultSchema>

/**
 * As QUATRO frases canônicas dos motivos da rubrica §4.6 — transcritas sem
 * alteração, indexadas pelo id do motivo.
 *
 * NÃO é campo do schema: a forma do JSON não muda. É texto de rubrica num sítio
 * só, para o prompt (17.3), o relatório (17.4), a vitrine (17.5) e os testes
 * lerem a MESMA frase em vez de a redigitar quatro vezes. É também o que torna
 * o motivo **assertável**: um teste pode exigir que o `rationale` comece por uma
 * destas quatro frases, coisa que prosa totalmente livre não permitiria.
 *
 * Repare que a quarta NÃO diz "Não observada" — ela acompanha um nível 3. Ver a
 * tabela em {@link AssessedCompetencySchema}.
 */
export const BEHAVIORAL_MOTIVE_SENTENCES = {
  'pergunta-nao-feita': 'Não observada — a pergunta não foi feita nesta entrevista',
  'sem-follow-up-de-acao':
    'Não observada — o episódio existe, mas não se sabe o que esta pessoa fez nele',
  'sem-episodio':
    'Não observada — a pergunta foi feita, mas a conversa não chegou a um caso concreto',
  'sem-follow-up-de-resultado':
    'Nível 3 — o nível 4 exigia o desfecho, e o desfecho não chegou a ser perguntado',
} as const

/** O id de um dos quatro motivos da rubrica §4.6. */
export type BehavioralMotive = keyof typeof BEHAVIORAL_MOTIVE_SENTENCES

/**
 * Rubrica comportamental PADRÃO — transcrição fiel da rubrica v0
 * (`docs/metodologia-comportamental/rubrica-v0.md`): as quatro competências da
 * §1, com a definição, o `evidenceHint` e as quatro âncoras BARS da §2. O texto
 * é portado, não reescrito — o conteúdo foi decidido na story 17.1, e qualquer
 * mudança de redação começa lá, no documento, não aqui.
 *
 * É o valor que o editor mostra pré-preenchido e o que `resolveBehavioralConfig`
 * devolve quando o assessment ainda não tem config própria — assim toda
 * entrevista já existente ganha a avaliação comportamental sem backfill de
 * dados, e quem quiser customizar sobrescreve.
 *
 * ⚠️ "v0" está no nome do documento por um motivo: as âncoras derivam dos
 * indicadores comportamentais do SHL Universal Competency Framework, não de
 * incidentes críticos colhidos no acervo — que ainda não tem volume para isso.
 * É melhor do que a escala 0–10 anterior e pior do que será quando houver
 * material (D4 do epic: construção por incidente crítico e calibração por
 * conjunto de referência estão adiadas, não esquecidas).
 */
export const DEFAULT_BEHAVIORAL_CONFIG: BehavioralConfig = {
  enabled: true,
  title: 'Rubrica v0 — quatro competências, quatro níveis',
  objective:
    'Este é o texto operacional da avaliação comportamental: as quatro competências, as dezesseis ' +
    'âncoras que decidem o nível, as cinco perguntas que produzem a evidência e as regras de ' +
    'pontuação. Substitui a nota única de 0 a 10 que hoje sai de uma escala gráfica holística. ' +
    'Como se usa: não se escolhe um número. Lê-se a evidência acumulada da competência e ' +
    'pergunta-se uma coisa só — qual destas quatro descrições corresponde melhor ao que está aqui? ' +
    'O nível é o resultado dessa correspondência, e a citação que a sustenta vai junto.',
  // Rubrica §3, "Alavanca em voo". A FORMULAÇÃO é o conteúdo: nunca "deu
  // certo?", que se responde com um sim e não produz evidência nenhuma —
  // perguntar *como souberam* obriga a nomear o sinal. E o advérbio "exatamente"
  // com o pronome "você" juntos deixam a resposta genérica sem saída. Reescrever
  // estas duas frases é mudar o método, não o texto.
  inFlightFollowUps: [
    'Nessa parte, o que exatamente você fez?',
    'E como vocês souberam que funcionou?',
  ],
  competencies: [
    {
      id: 'comunicacao',
      name: 'Comunicação e estruturação de raciocínio',
      definition:
        'Organiza o que sabe numa sequência que outra pessoa consegue seguir, desce do geral para o exemplo concreto e marca a fronteira entre o que executou e o que conhece de ouvir falar.',
      evidenceHint:
        'Leia a forma das respostas longas ao longo de toda a entrevista: se a resposta tem encadeamento próprio, se o exemplo concreto vem sem ser pedido, e se a pessoa diz alguma vez "não sei" ou "isso eu nunca usei".',
      // SEM `question` e SEM `followUps`, de propósito — não é lacuna. Rubrica
      // §2: "É a única competência que não depende de episódio narrado. Lê-se na
      // forma de falar ao longo de toda a entrevista, e uma entrevista normal já
      // traz amostra de sobra." Dar-lhe uma pergunta dedicada faria o roteiro
      // prometer um episódio que esta competência não usa.
      levels: [
        {
          level: 1,
          description:
            'Começa a responder, muda de assunto no meio e a pergunta fica sem resposta; ou responde com o nome da tecnologia e mais nada — "usei Kafka" — sem dizer para quê. Quando o entrevistador diz que não entendeu, repete as mesmas palavras. Fala do que executou e do que apenas ouviu falar no mesmo tom de domínio, sem separar as duas coisas.',
        },
        {
          level: 2,
          description:
            'Responde ao que foi perguntado, mas a resposta só se sustenta para quem já conhece o assunto: usa termos sem os traduzir e salta passos do raciocínio. Dá exemplo concreto quando lhe pedem um, não por iniciativa própria. Quando o entrevistador reformula a pergunta, a segunda resposta é claramente melhor que a primeira — a estrutura veio de quem perguntou, não de quem respondeu.',
        },
        {
          level: 3,
          description:
            'Enuncia o problema antes da solução, e o encadeamento é seguível por quem não conhece o sistema. Desce sozinha do geral para o caso concreto — "por exemplo, naquele projeto…" — sem que peçam. Diz ao menos uma vez "não sei" ou "isso eu nunca usei em produção", e distingue o que fez com as próprias mãos do que conhece de leitura.',
        },
        {
          level: 4,
          description:
            'Tudo do nível 3, e ajusta a explicação a quem está do outro lado: quando percebe que não foi entendida, troca de caminho — outra analogia, outro exemplo — em vez de repetir com mais ênfase. Nomeia o custo da própria escolha ("ganhamos velocidade e perdemos previsibilidade") sem que ninguém pergunte. Usa números, prazos e nomes de sistema quando eles existem, em vez de deixar tudo no plano geral.',
        },
      ],
    },
    {
      id: 'autonomia',
      name: 'Autonomia e ownership',
      definition:
        'Age além do que lhe foi formalmente pedido e acompanha a consequência do que entregou até depois de estar em uso.',
      evidenceHint:
        'Procure trechos em que a pessoa diz ter proposto, decidido ou continuado por conta própria — e se a narrativa segue até depois da entrega, ou termina no momento em que o código passou.',
      // Rubrica §3, Pergunta 3. Vale menos pelo episódio e mais pela reação:
      // quem não consegue nomear uma falha própria em toda a carreira está
      // administrando a impressão. Alimenta também `adaptacao` (o que mudou
      // depois), mas fica AQUI porque `adaptacao` já tem a sua (Pergunta 2) e o
      // contrato dá uma pergunta dedicada por competência.
      question: 'Me conta de uma entrega sua que deu errado.',
      // O follow-up separa arrependimento de aprendizado: quem só lamenta
      // responde "eu deveria ter me esforçado mais"; quem aprendeu responde com
      // uma mudança de prática.
      followUps: ['O que você faria diferente?'],
      levels: [
        {
          level: 1,
          description:
            'Descreve o trabalho sempre como algo que chegou pronto — "me passaram a tarefa", "o líder decidiu" — e não cita nenhuma vez em que tenha proposto algo. Diante de um bloqueio, a narrativa termina na espera: ficou parado até alguém responder, e não diz o que fez nesse intervalo. Não consegue apontar uma parte do sistema pela qual respondia.',
        },
        {
          level: 2,
          description:
            'Cita algo que fez por iniciativa própria, mas dentro do que já lhe tinha sido pedido: melhorou o que estava na tarefa, não foi além dela. A narrativa termina no momento em que o código passou — o que aconteceu depois, no uso real, aparece como assunto de outra pessoa. Quando encontrou um problema fora do próprio escopo, avisou e parou.',
        },
        {
          level: 3,
          description:
            'Cita ao menos um caso em que decidiu fazer algo que ninguém tinha pedido, e diz qual foi a decisão e por que a tomou. Acompanhou a entrega até depois de estar em uso, e sabe dizer se funcionou e como soube. Quando ficou bloqueada, diz a quem foi buscar a resposta e o que fez enquanto esperava.',
        },
        {
          level: 4,
          description:
            'Tudo do nível 3, e a iniciativa mudou algo além da própria tarefa: um processo, uma prática do time, um combinado que passou a valer para os outros. Assume um resultado ruim em primeira pessoa quando é o caso, e diz o que fez em seguida. Sabe dizer o que a mudança custou a quem não era ela — o tempo de outra pessoa, uma reescrita, uma migração.',
        },
      ],
    },
    {
      id: 'colaboracao',
      name: 'Colaboração e discordância',
      definition:
        'Leva o desacordo à pessoa certa, argumenta, e convive com a decisão que sai da conversa — inclusive quando não é a sua.',
      evidenceHint:
        'Procure o episódio em que a pessoa discordou de alguém com nome e função, o que ela disse a essa pessoa, e como a decisão terminou.',
      // Rubrica §3, Pergunta 1. A discordância é a sonda de colaboração mais
      // diagnóstica que existe — separa quem evita, quem impõe e quem negocia.
      // NÃO se pergunta por "conflito" (a palavra carrega julgamento) nem "você
      // é colaborativo?" (autorrelato em alta aposta não tem valor de
      // evidência): o fecho "o que você fez" pede uma ação, não uma opinião.
      question:
        'Me conta de uma vez em que você discordou de uma decisão técnica do time. O que você fez?',
      // O primeiro força a existência de uma pessoa real em vez de uma
      // abstração; o segundo fecha o desfecho e revela se a pessoa aceita perder
      // um argumento.
      followUps: ['Quem estava do outro lado?', 'Como terminou?'],
      levels: [
        {
          level: 1,
          description:
            'Não consegue citar uma situação concreta de discordância, ou a descreve como algo que lhe foi imposto, sem participação própria. Diante de uma objeção, repete o argumento inicial ou muda de assunto.',
        },
        {
          level: 2,
          description:
            'Cita uma discordância real, mas resolve por acomodação ou contorno: aceitou sem discutir, ou seguiu por fora. Não descreve conversa com a pessoa do outro lado.',
        },
        {
          level: 3,
          description:
            'Levou a discordância a quem de direito e apresentou argumento. Descreve a conversa e a decisão final — inclusive situações em que perdeu o argumento e seguiu a decisão do time.',
        },
        {
          level: 4,
          description:
            'Tudo do nível 3, e procurou entender a razão do outro lado antes de argumentar. Mudou de posição quando a informação mudou, e sabe dizer o efeito que a decisão teve no trabalho depois dela.',
        },
      ],
    },
    {
      id: 'adaptacao',
      name: 'Adaptação e ambiguidade',
      definition:
        'Reduz a incerteza com um passo nomeável antes de avançar, e refaz o caminho quando a informação nova contraria o plano.',
      evidenceHint:
        'Procure o que a pessoa nomeia ter feito **antes** de começar a codificar quando o pedido estava vago — a quem recorreu, o que leu, que teste pequeno fez — e o que fez quando a informação chegou depois.',
      // Rubrica §3, Pergunta 2. A segunda metade — "antes de começar a
      // codificar" — é deliberada: impede a resposta genérica "eu fui fazendo" e
      // obriga a nomear o passo de redução da ambiguidade, que é justamente o
      // que a âncora de nível 3 pede.
      question:
        'Me conta de uma vez em que você pegou uma tarefa e não estava claro o que era pra fazer. O que você fez antes de começar a codificar?',
      // O primeiro distingue quem procura contexto de quem assume e segue; o
      // segundo fecha o desfecho.
      followUps: ['A quem você recorreu?', 'E o que aconteceu depois?'],
      levels: [
        {
          level: 1,
          description:
            'Não cita nenhuma situação em que a informação faltasse, ou trata a falta como culpa de quem escreveu o requisito e não diz o que fez com isso. Quando o cenário mudou, continuou fazendo o que já fazia até alguém mandar parar. Não nomeia nenhum passo dado antes de começar.',
        },
        {
          level: 2,
          description:
            'Diante da tarefa pouco clara, escolheu uma interpretação e começou: a suposição existiu, mas não foi dita a ninguém nem escrita em lugar nenhum. Ou perguntou uma vez, não obteve resposta e seguiu assim mesmo. O retrabalho, quando aparece na história, é narrado como surpresa.',
        },
        {
          level: 3,
          description:
            'Nomeia o que fez antes de codificar: a quem perguntou, o que leu, que teste pequeno fez para descobrir o comportamento real. Diz qual era a dúvida concreta, e não apenas que "estava confuso". Quando a informação chegou e contrariava o plano, refez — e diz o que refez.',
        },
        {
          level: 4,
          description:
            'Tudo do nível 3, e deixou a suposição registrada onde outra pessoa pudesse contestá-la: escreveu no card, mandou a mensagem, combinou o critério antes de avançar. Escolheu de propósito por onde começar para descobrir cedo se estava errada, em vez de começar pela parte mais fácil. Sabe dizer o que a ambiguidade custou e o que passou a fazer diferente na tarefa seguinte.',
        },
      ],
    },
  ],
}

/**
 * Resolve a rubrica efetiva de um assessment.
 *
 * `null`/`undefined` (assessment nunca configurado) cai no
 * {@link DEFAULT_BEHAVIORAL_CONFIG} — é isso que faz toda entrevista já
 * existente herdar a avaliação comportamental sem migração de dados. Uma config
 * gravada, ainda que com `enabled: false`, tem precedência sobre o default.
 *
 * Valor gravado inválido (schema mudou, escrita manual no D1) NÃO derruba o
 * pipeline: cai no default e segue — degradação graciosa, mesma doutrina do
 * resto do código.
 */
export function resolveBehavioralConfig(stored: unknown): BehavioralConfig {
  if (stored === null || stored === undefined) return DEFAULT_BEHAVIORAL_CONFIG
  const parsed = BehavioralConfigSchema.safeParse(stored)
  return parsed.success ? parsed.data : DEFAULT_BEHAVIORAL_CONFIG
}

// ============================================================
// PRESET DE RUBRICA COMPORTAMENTAL (migration 0044, Story 19.1)
// Desenho: docs/superpowers/specs/2026-09-01-preset-comportamental-design.md
//
// Uma rubrica afinada, guardada para se aplicar a outra entrevista. A decisão 1
// do owner é CÓPIA, NÃO VÍNCULO: aplicar um preset copia a rubrica para a
// entrevista, que passa a ser dona dela — editar o preset depois não mexe em
// nenhuma entrevista já criada, e é por isso que não há id de preset guardado
// em `assessments` nem FK nenhuma entre as duas tabelas.
// ============================================================

/**
 * O id RESERVADO da "Padrão Genesis" — o preset que NÃO é uma linha na base.
 *
 * A lista que `GET /api/behavioral-presets` devolve é
 * `[padrão virtual] + linhas da tabela`, e o padrão vem sempre daqui:
 * {@link DEFAULT_BEHAVIORAL_CONFIG}, o mesmo objecto que o editor mostra
 * pré-preenchido e que {@link resolveBehavioralConfig} devolve a toda entrevista
 * sem config própria. Semeá-lo como linha duplicaria a rubrica padrão em dois
 * sítios e deixá-los-ia divergir em silêncio — ver o cabeçalho da migration
 * 0044, que proíbe esse atalho pelo nome.
 *
 * Consequência: `PATCH`/`DELETE` sobre este id respondem **400**, e não 404. O
 * recurso EXISTE (a lista devolve-o sempre, primeiro); o que é inválido é a
 * mutação. Um 404 diria a quem chama uma coisa falsa sobre o mundo.
 *
 * A constante é partilhada entre a API (que a compara) e a SPA (que reconhece o
 * padrão na lista para o marcar como tal). Escrever `'default'` à mão de um dos
 * lados é como o predicado do 409 se partiu na 0039: um literal não acompanha
 * uma mudança, e o `tsc` não vê dentro de uma string.
 */
export const DEFAULT_BEHAVIORAL_PRESET_ID = 'default'

/**
 * Um preset tal como a API o devolve.
 *
 * ⚠️ `createdAt`/`updatedAt` são NULÁVEIS, e não por preguiça: o padrão virtual
 * não é uma linha, portanto não tem instante de nascimento nenhum. As
 * alternativas seriam inventar um (`new Date(0)`, ou "agora") — e um instante
 * inventado é pior que um `null`, porque uma lista ordenada por data passaria a
 * ordenar-se por uma mentira sem que nada ficasse vermelho. Toda linha REAL tem
 * os dois preenchidos (as colunas são `NOT NULL` na 0044); o `null` identifica,
 * sozinho, o preset que não existe na base.
 *
 * Datas em ISO (string), como todo o resto dos read models desta casa — é o que
 * o `c.json()` produz a partir de um `Date` do Drizzle.
 */
export const BehavioralPresetSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1).max(120),
  description: z.string().nullable(),
  config: BehavioralConfigSchema,
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
})
export type BehavioralPreset = z.infer<typeof BehavioralPresetSchema>

/**
 * O item da lista — o preset mais a marca de quem é o padrão.
 *
 * `isDefault` não vive em {@link BehavioralPresetSchema} porque não é um atributo
 * do preset: é uma afirmação sobre a ORIGEM daquele item na resposta (veio do
 * `dtos.ts` ou veio da tabela). Comparar o `id` com
 * {@link DEFAULT_BEHAVIORAL_PRESET_ID} dá a mesma informação — o campo existe
 * para que a tela não tenha de saber disso, exactamente como
 * `GET /api/assessments/:id/behavioral-config` já devolve o seu próprio
 * `isDefault` em vez de mandar a SPA comparar rubricas.
 */
export const BehavioralPresetListItemSchema = BehavioralPresetSchema.extend({
  isDefault: z.boolean(),
})
export type BehavioralPresetListItem = z.infer<typeof BehavioralPresetListItemSchema>

/**
 * Limite do nome. 120 é o mesmo teto de `Competency.name` — um rótulo de lista,
 * não um parágrafo.
 */
const behavioralPresetNameShape = z.string().trim().min(1).max(120)

/**
 * Limite da descrição. 600 é o mesmo teto de `Competency.definition`: uma linha
 * ou duas a dizer para que serve este preset, não um segundo objectivo.
 */
const behavioralPresetDescriptionShape = z.string().trim().max(600)

/**
 * `POST /api/behavioral-presets`.
 *
 * `config` é validada AQUI, pelo mesmo `BehavioralConfigSchema` que o editor
 * usa: nunca deixa nascer um preset que não parseia. É a metade servidora da
 * AC-9 — a SPA desabilita o botão com `firstProblem()`, e esta linha garante que
 * um cliente que ignore o botão leva 400.
 *
 * `name_normalized` NÃO está aqui de propósito: é calculada pelo servidor a
 * partir do `name`, nunca vem do cliente. O `.strict()` recusa-a explicitamente,
 * junto com qualquer outra chave desconhecida.
 */
export const CreateBehavioralPresetSchema = z
  .object({
    name: behavioralPresetNameShape,
    description: behavioralPresetDescriptionShape.optional(),
    config: BehavioralConfigSchema,
  })
  .strict()
export type CreateBehavioralPreset = z.infer<typeof CreateBehavioralPresetSchema>

/**
 * `PATCH /api/behavioral-presets/:id` — atualização parcial.
 *
 * `description` aceita `null` para PODER ser limpa: sem isso, uma descrição
 * escrita por engano ficaria lá para sempre (o `.partial()` sozinho só permite
 * OMITIR o campo, que significa "não mexer", não "apagar"). `name` e `config`
 * não aceitam `null` — um preset sem nome ou sem rubrica não é um preset.
 *
 * O `.refine()` do corpo vazio existe porque um `PATCH {}` é quase sempre um
 * defeito do chamador, e responder 200 a ele esconde-o.
 */
export const UpdateBehavioralPresetSchema = z
  .object({
    name: behavioralPresetNameShape,
    description: behavioralPresetDescriptionShape.nullable(),
    config: BehavioralConfigSchema,
  })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateBehavioralPreset = z.infer<typeof UpdateBehavioralPresetSchema>

// ============================================================
// PORTAL DO CLIENTE, F2 — ALOCAÇÃO e APONTAMENTO (migration 0034)
// Spec: docs/superpowers/specs/2026-08-05-portal-do-cliente-design.md §3.1/§5.
// ============================================================

/**
 * Data de CONTRATO — `YYYY-MM-DD`, nunca um instante.
 *
 * O regex sozinho aceitaria `2026-02-31`, portanto há um `.refine()` a seguir que
 * pede ao `Date` para confirmar que a data EXISTE (e que não foi normalizada para
 * 1 de março em silêncio, que é o que o construtor faz). Ver o cabeçalho da
 * migration 0034 para a razão de a coluna ser texto e não epoch.
 */
export const ContractDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use o formato AAAA-MM-DD.')
  .refine((v) => {
    // `Date.parse` de 'YYYY-MM-DD' é UTC por especificação, logo comparar com o
    // `toISOString().slice(0, 10)` devolve a MESMA cadeia quando a data existe —
    // e uma diferente quando o mês transbordou.
    const parsed = new Date(`${v}T00:00:00.000Z`)
    return !Number.isNaN(parsed.getTime()) && parsed.toISOString().slice(0, 10) === v
  }, 'Data inexistente no calendário.')

/**
 * Criar uma alocação — a operação que ABRE a um cliente a vista identificada de
 * uma pessoa (D2).
 *
 * ⚠️ `applicationId` É OBRIGATÓRIO, e a mudança é de REGRA e não de higiene.
 * Antes era opcional, com a "alocação directa" (sem candidatura) documentada como
 * caminho legítimo. Deixou de ser: uma alocação só existe depois de uma
 * candidatura marcada como CONTRATADA. Não é uma condição sobre o id que veio — é
 * a exigência de que ele venha, e sem ele o pedido é 400.
 *
 * O INVERSO CONTINUA SOLTO, e é deliberado: aprovar uma candidatura NÃO cria
 * alocação nenhuma. A oferta que aparece ao aprovar (`AllocationOffer`) é um
 * convite dispensável — "Agora não" fecha-a e a candidatura fica contratada na
 * mesma. Contratar é pré-requisito de alocar; alocar não é consequência de
 * contratar.
 *
 * A ROTA continua a ser quem valida que a candidatura é DAQUELA PESSOA e que
 * está `hired` — um schema não consegue nenhuma das duas: as duas precisam da
 * base. Aqui só se exige que o campo exista.
 *
 * ⚠️ E A COLUNA `allocations.application_id` CONTINUA NULLABLE no D1 — ver o bloco
 * por extenso em `packages/db/postgres/schema/allocations.ts`. O invariante vive no
 * servidor, como todos os outros deste esquema.
 *
 * `endedOn` NÃO entra aqui de propósito. Criar uma alocação já terminada é
 * possível (é como se regista o histórico anterior à migration), mas fazê-lo pelo
 * mesmo caminho que abre acesso misturaria duas intenções opostas num só pedido.
 * Regista-se com `POST` e termina-se com `PATCH`, que é o caminho que já existe
 * e que audita a data de fim como uma mudança.
 *
 * ⚠️ A pessoa vem em `professionalId`, e só nesse nome — ver o bloco do id da
 * pessoa junto a `CreateApplicationSchema`. O `.strict()` recusa tudo o resto,
 * o nome antigo `candidateId` incluído: mandá-lo é 400, e não uma aceitação
 * calada.
 */
export const CreateAllocationSchema = z
  .object({
    customerId: z.string().min(1),
    ...idDaPessoaShape,
    // Sem `.nullable()` e sem `.optional()`: os dois eram a alocação directa, e a
    // alocação directa deixou de existir.
    applicationId: z.string().min(1),
    roleTitle: z.string().min(1).max(200),
    startedOn: ContractDateSchema,
    // A PRIMEIRA atribuição (Story 24.4, migration 0052): área e/ou gestor, que
    // nascem com a alocação a partir de `startedOn`. Opcionais — sem nenhum dos
    // dois, a alocação nasce sem atribuição. Ver `AllocationAssignment` abaixo.
    areaId: z.string().min(1).nullable().optional(),
    managerId: z.string().min(1).nullable().optional(),
  })
  .strict()
export type CreateAllocation = z.infer<typeof CreateAllocationSchema>

/**
 * Atualização parcial. O par (cliente, pessoa) é IMUTÁVEL — realocar é terminar
 * uma alocação e criar outra, porque são dois factos e não uma correção.
 *
 * `endedOn: null` REABRE uma alocação encerrada (ou cancela o término marcado de
 * uma viva), e uma data FUTURA numa encerrada também a reabre: a alocação está viva
 * até à véspera do término (Story 28.1). Reabrir e cancelar um término são de
 * admin; encerrar é de admin e de recrutador — a rota decide pelo corpo e pela
 * linha (`routes/allocations.ts`).
 *
 * ⚠️ A COERÊNCIA DAS DUAS DATAS (`endedOn >= startedOn`, Story 29.1) é da ROTA, e
 * não deste esquema: um `PATCH` só com `startedOn` pode passar para depois de um
 * término que já está guardado, e só a rota tem a linha. Um `refine` aqui veria as
 * duas datas apenas quando o pedido as traz juntas.
 *
 * `.strict()` aqui (e não no apontamento abaixo) porque não há invariante nenhum
 * que um campo a mais possa tentar contornar: recusar o desconhecido é a postura
 * certa quando recusar não esconde nada.
 */
export const UpdateAllocationSchema = z
  .object({
    roleTitle: z.string().min(1).max(200).optional(),
    startedOn: ContractDateSchema.optional(),
    endedOn: ContractDateSchema.nullable().optional(),
    endReason: z.string().max(2000).nullable().optional(),
  })
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateAllocation = z.infer<typeof UpdateAllocationSchema>

/**
 * O que o apontamento é. Espelha o enum da coluna (sem `CHECK` no D1).
 *
 * ⚠️ `milestone` ("Marco") é o quarto valor e entrou com o Caderno. Não precisou
 * de migration: a coluna é `TEXT` sem `CHECK`, que é precisamente a postura que a
 * 0025/0034/0036 tomaram para que acrescentar um valor custasse uma linha e não um
 * table rebuild. Aqui está a prova de que a postura pagou — e o quinto,
 * `one_on_one` ("1:1", Story 31.4), entrou pelo mesmo caminho, como o sexto,
 * `review` ("Avaliação", Story 34.3).
 *
 * UM enum para a coluna, e não dois: duas listas para a mesma coluna seriam duas
 * verdades — uma delas a poder mentir. O que se pode ESCREVER pelo POST de notas é
 * um SUBCONJUNTO derivado deste (`WritableNoteKindSchema` e `CustomerNoteKindSchema`,
 * abaixo), e não uma segunda lista. Este é o da LEITURA.
 */
export const AllocationNoteKindSchema = z.enum([
  'praise',
  'concern',
  'note',
  'milestone',
  'one_on_one',
  'review',
])
export type AllocationNoteKind = z.infer<typeof AllocationNoteKindSchema>

/**
 * O que se escreve pelo POST de notas (portal, alocação interna e caderno): todas
 * as espécies menos a Avaliação.
 *
 * ⚠️ `review` SÓ O ENDPOINT DE AVALIAÇÃO O ESCREVE (Story 34.3), na mesma escrita
 * atómica que grava as 4 notas (`POST /api/portal/allocations/:id/reviews`). Um
 * apontamento «Avaliação» escrito à mão seria uma avaliação sem notas — o Journal
 * diria que o cliente avaliou, e o score não saberia de nada. Nenhum compositor o
 * oferece, e o servidor recusa-o com 400 em todas as portas de notas.
 */
export const WritableNoteKindSchema = AllocationNoteKindSchema.exclude(['review'])
export type WritableNoteKind = z.infer<typeof WritableNoteKindSchema>

/**
 * O que o CLIENTE pode escrever: as espécies escrevíveis menos o 1:1.
 *
 * ⚠️ O 1:1 É A PRIMEIRA ESPÉCIE QUE O SERVIDOR PROÍBE AO CLIENTE, e não só uma que
 * o portal não oferece (como o `milestone`, que um cliente pode vir a marcar). Um
 * 1:1 é uma conversa da GENESIS com a pessoa, e é a data dele que diz na lista de
 * profissionais quem está há tempo demais sem conversa («1:1 devido», Story 31.4).
 * Um 1:1 escrito pelo cliente desligaria esse aviso sem que ninguém da Genesis
 * tivesse falado com a pessoa. Esconder a espécie no compositor do portal não
 * chega: o pedido faz-se à mão.
 *
 * O cliente continua a LER um 1:1 partilhado — a leitura usa o enum inteiro.
 */
export const CustomerNoteKindSchema = WritableNoteKindSchema.exclude(['one_on_one'])

/**
 * Quantas ações do plano um apontamento pode citar (Story 37.2). Um teto para o
 * corpo do pedido, e não uma regra de negócio: um plano tem poucas ações, e quem
 * cita mais de vinte está a descrever o plano, não um momento.
 */
export const NOTE_ACTIONS_MAX = 20
export type CustomerNoteKind = z.infer<typeof CustomerNoteKindSchema>

/**
 * Quem vê o apontamento. TRÊS níveis, e cada um exclui um leitor a mais:
 *
 *   • `shared`   — o cliente lê (enquanto a passagem estiver viva);
 *   • `internal` — o caderno da Genesis; o cliente não o lê;
 *   • `private`  — SÓ O AUTOR. Nem outro usuário da Genesis, nem um admin.
 *
 * ⚠️ `private` NÃO TEM ESCOTILHA DE ADMIN, e a ausência é deliberada — o pedido
 * do owner foi "nem mesmo outros usuários genesis conseguem visualizar", e uma
 * escotilha transformaria o nível numa promessa que não se cumpre. A consequência
 * está assumida: um apontamento privado cujo autor sai da empresa fica ilegível
 * para sempre. A cascata da LGPD continua a apagá-lo (apaga por `professional_id`,
 * não por autor) — ver `apps/api/src/lib/professional-notes.ts`.
 */
export const AllocationNoteVisibilitySchema = z.enum(['shared', 'internal', 'private'])
export type AllocationNoteVisibility = z.infer<typeof AllocationNoteVisibilitySchema>

/**
 * O corpo de um apontamento escrito PELO CLIENTE.
 *
 * ⚠️ SEM `.strict()`, E ISSO É A DECISÃO DE SEGURANÇA — não um esquecimento.
 *
 * Os invariantes do servidor são `author_side='customer'` e `visibility='shared'`,
 * SEMPRE (spec §3.1). Há duas formas de os impor contra um corpo que tente o
 * contrário:
 *
 *   a) `.strict()` → 400 quando o corpo traz `visibility`. Fecha a porta de HOJE,
 *      mas prova apenas que o schema recusou. No dia em que alguém tirar o
 *      `.strict()` (para aceitar um campo novo qualquer), a recusa desaparece e o
 *      handler passa a receber `visibility` num objeto que ele pode espalhar.
 *   b) não declarar os campos → o zod DESCARTA-OS, e o handler escreve literais.
 *      O corpo pode trazer `visibility: 'internal'` e `authorSide: 'genesis'` à
 *      vontade: eles não existem em `parsed.data`, portanto nem um
 *      `...parsed.data` distraído no `insert` os faria chegar à tabela.
 *
 * Escolhe-se (b): a garantia que interessa é a que sobrevive ao refactor. O preço
 * é um campo mal escrito ser ignorado em silêncio em vez de dar 400 — e esse
 * preço paga-se de olhos abertos, porque o campo em causa é o que decide se o
 * caderno interno da Genesis fica visível ao cliente.
 */
export const CreateAllocationNoteSchema = z.object({
  // O subconjunto do cliente: um 1:1 vindo do portal é 400 (ver `CustomerNoteKindSchema`).
  kind: CustomerNoteKindSchema.optional(),
  body: z.string().min(1).max(5000),
  occurredAt: z.string().datetime().optional(),
  // As ações do plano da passagem de que o apontamento fala (Story 37.2). Nos três
  // schemas, por herança: o portal também liga. Os repetidos saem aqui, uma vez; que
  // as ações são da passagem da nota, confere-o o servidor (`gravarApontamento`).
  actionIds: z
    .array(z.string().min(1))
    .max(NOTE_ACTIONS_MAX)
    .transform((ids) => [...new Set(ids)])
    .optional(),
})
export type CreateAllocationNote = z.infer<typeof CreateAllocationNoteSchema>

/**
 * O corpo de um apontamento escrito PELA GENESIS. É o de cima MAIS `visibility` —
 * o único lado que escolhe se o que escreve é partilhado ou interno — e com
 * `kind` alargado às espécies escrevíveis: a Genesis regista o 1:1, que o cliente
 * não pode escrever. ⚠️ O `kind` redeclarado aqui é o que impede o subconjunto do
 * cliente de chegar, por herança, ao caderno e à rota da alocação — e é
 * `WritableNoteKindSchema`, e não o enum inteiro: a Avaliação não se escreve à mão
 * de nenhum dos lados (Story 34.3).
 *
 * Também sem `.strict()`, e pela mesma razão pelo outro motivo: `authorSide`
 * continua a não ser declarado em lado nenhum, portanto NENHUM dos dois lados o
 * pode enviar. O lado deriva-se da rota (portal ⟹ `customer`, interna ⟹
 * `genesis`), e a rota deriva-se do middleware que a guarda.
 */
export const CreateInternalAllocationNoteSchema = CreateAllocationNoteSchema.extend({
  kind: WritableNoteKindSchema.optional(),
  visibility: AllocationNoteVisibilitySchema.optional(),
})
export type CreateInternalAllocationNote = z.infer<typeof CreateInternalAllocationNoteSchema>

/**
 * O que o CADERNO DO PROFISSIONAL escreve (migration 0036).
 *
 * É o schema da Genesis MAIS `allocationId` — a passagem de origem, OPCIONAL e
 * ANULÁVEL. Um apontamento sobre a pessoa pode não pertencer a passagem nenhuma
 * (uma conversa entre alocações, uma observação do processo seletivo), e NULL
 * significa "fora de alocação", não "dado em falta". É por isso que ele é
 * `.nullable()` e não só `.optional()`: os dois casos são distintos do lado de
 * quem escreve — omitir é "não indiquei", enviar `null` é "não há".
 *
 * ⚠️ Também SEM `.strict()`, e pela razão escrita por extenso em
 * `CreateAllocationNoteSchema` acima: `authorSide` continua a não ser declarado
 * em schema nenhum, portanto nenhum dos dois lados o pode enviar. O lado deriva
 * da ROTA, e a rota deriva do middleware que a guarda.
 */
export const CreateProfessionalNoteSchema = CreateInternalAllocationNoteSchema.extend({
  allocationId: z.string().min(1).nullable().optional(),
})
export type CreateProfessionalNote = z.infer<typeof CreateProfessionalNoteSchema>

/** O tamanho máximo do motivo de uma exclusão de apontamento (Story 37.4). */
export const NOTE_DELETION_REASON_MAX = 500

/**
 * O corpo de `POST …/notes/:noteId/deletion` (Story 37.4): só o motivo, obrigatório.
 * Quem exclui, quando e de que nota vêm da sessão, do servidor e da rota — nunca do
 * corpo, que nem tem campo onde caibam.
 */
export const DeleteNoteSchema = z.object({
  reason: z.string().trim().min(1).max(NOTE_DELETION_REASON_MAX),
})
export type DeleteNote = z.infer<typeof DeleteNoteSchema>

// ============================================================
// OPERAÇÕES, GESTORES, ATRIBUIÇÕES E O ACESSO DO PORTAL
// (migration 0052, Story 24.4 — Epic 24; migration 0070, Story 38.1 — Epic 38)
// Spec: docs/superpowers/specs/2026-09-30-portal-areas-e-acesso-design.md §5.
//
// A «área/projeto» passou a OPERAÇÃO na Story 38.1. Os campos do fio que ainda
// dizem área (`areaId`, `portalAllAreas`, `portalAreaIds`) saem nas stories 38.5,
// 38.7 e 38.10.
//
// Só a Genesis cadastra operações, gestores e acessos (D9) — por isso nada daqui
// atravessa a barreira do portal. O que o portal vai MOSTRAR (24.6/24.7) deriva
// destes dados, mas tem contrato próprio.
// ============================================================

/** Ciclo de vida de uma operação ou de um gestor. Inativar, nunca apagar em uso. */
export const CUSTOMER_RECORD_STATUSES = ['active', 'inactive'] as const
export const CustomerRecordStatusSchema = z.enum(CUSTOMER_RECORD_STATUSES)
export type CustomerRecordStatus = z.infer<typeof CustomerRecordStatusSchema>

/** A unidade do bill rate de uma operação. */
export const BILL_RATE_UNITS = ['hour', 'month'] as const
export const BillRateUnitSchema = z.enum(BILL_RATE_UNITS)
export type BillRateUnit = z.infer<typeof BillRateUnitSchema>

/** A moeda do bill rate de uma operação. */
export const CURRENCIES = ['BRL', 'USD', 'EUR'] as const
export const CurrencySchema = z.enum(CURRENCIES)
export type Currency = z.infer<typeof CurrencySchema>

/** O teto de gestores vinculados a uma operação num pedido. */
export const OPERATION_MANAGERS_MAX = 100

/** O nome de uma operação. Único por cliente, sem distinguir maiúsculas (409). */
const CustomerOperationNameSchema = z.string().trim().min(1).max(200)

const operationShape = {
  name: CustomerOperationNameSchema,
  billRateUnit: BillRateUnitSchema,
  currency: CurrencySchema,
  /** Um usuário Genesis ativo; `null` = sem Delivery Manager. */
  deliveryManagerUserId: z.string().min(1).nullable(),
  /** O conjunto inteiro dos gestores vinculados. A API tira os ids repetidos. */
  managerIds: z.array(z.string().min(1)).max(OPERATION_MANAGERS_MAX),
} as const

/**
 * `POST /api/customers/:id/operations`. Nasce ativa. Só o nome é obrigatório: sem os
 * outros, o bill rate é por mês em reais, sem Delivery Manager e sem gestores.
 */
export const CreateCustomerOperationSchema = z
  .object({
    name: operationShape.name,
    billRateUnit: operationShape.billRateUnit.optional(),
    currency: operationShape.currency.optional(),
    deliveryManagerUserId: operationShape.deliveryManagerUserId.optional(),
    managerIds: operationShape.managerIds.optional(),
  })
  .strict()
export type CreateCustomerOperation = z.infer<typeof CreateCustomerOperationSchema>

/**
 * `PATCH /api/customers/:id/operations/:operationId`. Campo ausente = não muda:
 * `managerIds: []` tira todos os gestores, e `deliveryManagerUserId: null` fecha o
 * período atual do Delivery Manager. É isto que deixa a SPA da release anterior, que
 * envia só `{ name }` ou `{ status }`, sem apagar nada.
 */
export const UpdateCustomerOperationSchema = z
  .object({ ...operationShape, status: CustomerRecordStatusSchema })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateCustomerOperation = z.infer<typeof UpdateCustomerOperationSchema>

/** Uma operação como a API a devolve. */
export interface CustomerOperation {
  id: string
  customerId: string
  name: string
  status: CustomerRecordStatus
  billRateUnit: BillRateUnit
  currency: Currency
  /** O período atual; `null` = sem Delivery Manager. */
  deliveryManager: { userId: string; name: string; startedOn: string } | null
  /** Ordenados pelo nome do gestor. */
  managerIds: string[]
  createdAt: string
  updatedAt: string
}

/** Um período do Delivery Manager (`GET …/operations/:operationId/delivery-managers`). */
export interface OperationDeliveryManagerPeriod {
  userId: string
  name: string
  /** ISO `YYYY-MM-DD`. */
  startedOn: string
  /** Exclusivo, ISO `YYYY-MM-DD`; `null` = o atual. */
  endedOn: string | null
}

/**
 * O e-mail do gestor — NORMALIZADO (trim + minúsculas) antes de validar e de
 * gravar. Anulável: há gestores de quem só se sabe o nome. Um campo de formulário
 * deixado em branco deve chegar como `null`, não como `''` (que é 400).
 */
const CustomerManagerEmailSchema = z
  .string()
  .transform((v) => v.trim().toLowerCase())
  .pipe(z.string().email().max(254))

const customerManagerShape = {
  name: z.string().trim().min(1).max(150),
  email: CustomerManagerEmailSchema.nullable(),
  /**
   * O cargo no cliente («Head de Produto»), Story 34.2. Anulável, como o e-mail: um
   * campo deixado em branco chega como `null`, e não como `''` (que é 400).
   */
  title: z.string().trim().min(1).max(150).nullable(),
  /**
   * A conta do portal, SE o gestor tiver uma (D6). Tem de ser um usuário
   * `role='customer'` DO MESMO cliente — a API recusa com 422 o contrário. `null`
   * desliga a conta do gestor.
   */
  userId: z.string().min(1).nullable(),
} as const

/** `POST /api/customers/:id/managers`. Nasce ativo. */
export const CreateCustomerManagerSchema = z
  .object({
    name: customerManagerShape.name,
    email: customerManagerShape.email.optional(),
    title: customerManagerShape.title.optional(),
    userId: customerManagerShape.userId.optional(),
  })
  .strict()
export type CreateCustomerManager = z.infer<typeof CreateCustomerManagerSchema>

/** `PATCH /api/customers/:id/managers/:managerId` — editar e/ou inativar/reativar. */
export const UpdateCustomerManagerSchema = z
  .object({ ...customerManagerShape, status: CustomerRecordStatusSchema })
  .partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, 'At least one field must be provided')
export type UpdateCustomerManager = z.infer<typeof UpdateCustomerManagerSchema>

/** Um gestor como a API o devolve. */
export interface CustomerManager {
  id: string
  customerId: string
  name: string
  email: string | null
  /** O cargo no cliente; `null` = sem cargo. */
  title: string | null
  userId: string | null
  status: CustomerRecordStatus
  createdAt: string
  updatedAt: string
}

/**
 * `POST /api/allocations/:id/assignments` — a pessoa passa a outra área e/ou a
 * outro gestor A PARTIR DE `startedOn`, sem a alocação acabar (D12).
 *
 * A atual é encerrada com `ended_on = startedOn` e a nova inserida, no mesmo
 * batch. Pelo menos um dos dois tem de vir: uma atribuição sem área e sem gestor
 * não diz nada. Área e gestor têm de ser ATIVOS e do MESMO cliente da alocação
 * (422), e a alocação tem de estar viva (409).
 */
export const CreateAllocationAssignmentSchema = z
  .object({
    areaId: z.string().min(1).nullable().optional(),
    managerId: z.string().min(1).nullable().optional(),
    startedOn: ContractDateSchema,
  })
  .strict()
  .refine((v) => v.areaId != null || v.managerId != null, {
    message: 'Indique a operação, o gestor, ou os dois.',
    path: ['areaId'],
  })
export type CreateAllocationAssignment = z.infer<typeof CreateAllocationAssignmentSchema>

/** A área de uma atribuição, resolvida para a tela. */
export interface AssignmentArea {
  id: string
  name: string
}

/** O gestor de uma atribuição, resolvido para a tela. */
export interface AssignmentManager {
  id: string
  name: string
  email: string | null
}

/**
 * A atribuição ATUAL, como `GET /api/allocations` e `GET /api/allocations/:id` a
 * acrescentam a cada alocação. `null` nos dois = sem atribuição.
 */
export interface CurrentAssignment {
  area: AssignmentArea | null
  manager: AssignmentManager | null
}

/** Um período do histórico (`GET /api/allocations/:id/assignments`). */
export interface AllocationAssignment extends CurrentAssignment {
  id: string
  allocationId: string
  /** ISO `YYYY-MM-DD`. */
  startedOn: string
  /** ISO `YYYY-MM-DD`; `null` = a atual. */
  endedOn: string | null
  createdBy: string | null
  createdAt: string
}

/**
 * Teto de áreas num acesso. Um usuário que precise de mais do que isto quer, na
 * prática, "todas as áreas".
 */
export const PORTAL_ACCESS_AREAS_MAX = 200

/** A lista de áreas de um acesso: ids sem repetição. */
export const PortalAccessAreaIdsSchema = z
  .array(z.string().min(1))
  .max(PORTAL_ACCESS_AREAS_MAX)
  .refine((ids) => new Set(ids).size === ids.length, 'ids repetidos')

/**
 * O acesso do usuário do cliente (D7): todas as áreas, OU só estas.
 *
 * Corpo de `PUT /api/users/:id/portal-access` e resposta do `GET`. "Todas" não é
 * uma lista — `allAreas: true` com áreas é 400. `{ allAreas: false, areaIds: [] }`
 * é um estado legítimo: o usuário continua a ver as vagas e a vitrine (D8), e não
 * vê profissional nenhum. As áreas têm de ser ATIVAS e do cliente do usuário (422).
 */
export const PortalAccessSchema = z
  .object({ allAreas: z.boolean(), areaIds: PortalAccessAreaIdsSchema })
  .strict()
  .refine((v) => !v.allAreas || v.areaIds.length === 0, {
    message: '"Todas as operações" não leva lista de operações.',
    path: ['areaIds'],
  })
export type PortalAccess = z.infer<typeof PortalAccessSchema>

// ============================================================
// ANEXOS DO APONTAMENTO (migration 0037, Caderno do Profissional W4)
//
// O arquivo é o CORPO da nota noutro formato, e é isso que decide tudo o que
// está aqui: ele NÃO tem visibilidade própria (herda a do apontamento), NÃO tem
// caminho de exclusão (a nota é append-only, o anexo também), e o cliente NÃO o
// escreve (não há POST no portal — só a leitura do binário).
// ============================================================

/**
 * ⚠️ A ALLOWLIST DE TIPOS, E O CRITÉRIO QUE A FECHA.
 *
 * Documento e imagem. A regra que decide o que entra NÃO é "é um formato comum" —
 * é esta: **nenhum destes tipos é renderizável como DOCUMENTO HTML pelo browser.**
 *
 * É o mesmo critério (e a mesma frase) que governa a lista do CV externo em
 * `apps/api/src/routes/professionals.ts`, e a razão é a rota de leitura: o anexo é
 * servido INLINE, na PRÓPRIA ORIGEM e com o cookie de sessão, para se poder abrir
 * dentro da plataforma. Nessa postura, um tipo que o browser saiba interpretar
 * como documento com script é XSS armazenado na nossa origem.
 *
 * ⚠️ POR ISSO `image/svg+xml` NÃO ESTÁ AQUI, e a ausência é a decisão mais fácil
 * de "corrigir" por distração: um SVG é XML, um `<script>` cabe lá dentro, e
 * servido como documento na nossa origem ele corre com o cookie do utilizador. É
 * exactamente o vetor que `LOGO_SECURITY_HEADERS` (routes/customers.ts) precisa de
 * `sandbox` para conter. Quem quiser acrescentá-lo tem de acrescentar `sandbox` à
 * rota — e `sandbox` mata o visualizador de PDF embutido, que é o caso de uso
 * principal. As duas coisas não cabem na mesma rota; escolheu-se o PDF.
 *
 * `text/plain` fica, e não é uma excepção ao critério: com `nosniff` ele continua
 * `text/plain` e o browser não o pode re-adivinhar como HTML. É `nosniff` o
 * cabeçalho LOAD-BEARING desta rota — sem ele, um `.html` renomeado para `.txt`
 * seria XSS armazenado.
 */
export const NOTE_ATTACHMENT_CONTENT_TYPES = [
  // --- Documento ---
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  // --- Imagem ---
  'image/png',
  'image/jpeg',
  'image/webp',
] as const

export const NoteAttachmentContentTypeSchema = z.enum(NOTE_ATTACHMENT_CONTENT_TYPES)
export type NoteAttachmentContentType = z.infer<typeof NoteAttachmentContentTypeSchema>

/**
 * O TETO — 10 MiB.
 *
 * Entre os dois vizinhos e por uma razão de conteúdo: o CV são 5 MB (um documento
 * de texto com, no limite, uma fotografia) e o enunciado da prova são 20 MB (um
 * PDF paginado com imagens). Um anexo de apontamento é o print de uma conversa, o
 * PDF do feedback do cliente, a foto de um crachá — 10 MiB é folgado para
 * qualquer um deles e continua a caber num `arrayBuffer()` se um dia a rota
 * precisar de ver os bytes.
 *
 * ⚠️ É VALIDADO DUAS VEZES, e as duas contam: no presign contra o `sizeBytes`
 * DECLARADO (para não emitir um URL que autorize escrever 2 GB no bucket), e no
 * registo contra o tamanho REAL que o R2 devolve. Desde a Story 25.2 o URL
 * presignado assina o `content-length` = o `sizeBytes` declarado, portanto o R2
 * recusa um PUT maior do que o que passou aqui. É a segunda validação, contra o
 * objeto que existe, que decide o que se grava.
 */
export const NOTE_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024

/** Comprimento máximo do nome guardado — um nome de ficheiro, não um texto. */
export const NOTE_ATTACHMENT_NAME_MAX_LENGTH = 255

/**
 * O pedido de URL presignado — `POST …/notes/:noteId/attachments/presign`.
 *
 * `filename` entra aqui porque a chave do objecto leva um slug derivado dele (e
 * porque o nome é o que se guarda para o download). Ele é SANITIZADO no servidor:
 * o que vem no corpo é uma sugestão, não um caminho.
 *
 * ⚠️ SEM `.strict()`, pela mesma razão escrita por extenso em
 * `CreateAllocationNoteSchema`: `professionalId`, `r2Key` e `uploadedBy` não são
 * DECLARADOS em schema nenhum, portanto o zod descarta-os e nem um
 * `...parsed.data` distraído os faria chegar à tabela. A pessoa vem da linha do
 * apontamento; a chave é construída pelo servidor; o autor vem da sessão.
 */
export const PresignNoteAttachmentSchema = z.object({
  filename: z.string().min(1).max(NOTE_ATTACHMENT_NAME_MAX_LENGTH),
  contentType: NoteAttachmentContentTypeSchema,
  sizeBytes: z.number().int().positive(),
})
export type PresignNoteAttachment = z.infer<typeof PresignNoteAttachmentSchema>

/**
 * O registo depois do PUT — `POST …/notes/:noteId/attachments`.
 *
 * ⚠️ `r2Key` VEM DO CLIENTE, E ISSO É INEVITÁVEL: a chave leva um UUID gerado no
 * presign, portanto o servidor não a consegue re-derivar. O que ele consegue — e
 * faz — é RECUSÁ-LA quando não está sob o prefixo que o presign DAQUELA porta gera:
 * `note-attachments/<professionalId>/` no caderno, `note-attachments/portal/
 * <allocationId>/` no portal (Story 24.8 — sem o id da pessoa, que o cliente nunca
 * vê). Depois do prefixo, nenhum `/`. E uma chave JÁ registada é recusada com 409.
 *
 * Sem essa verificação, este endpoint seria um leitor universal do bucket:
 * bastava registar `candidates/<id>/cv.pdf` (chave de R2 LITERAL do CV externo,
 * anterior à 0039 — não é o nome da tabela) como "anexo" de uma nota própria para
 * o GET dos anexos servir o CV de alguém — com a ACL dos apontamentos, que é
 * outra e mais larga. Ver `attachmentKeyIsUnder` em
 * `apps/api/src/lib/professional-note-attachments.ts`.
 *
 * `contentType` e `sizeBytes` também vêm do cliente e também não são de confiança:
 * o servidor faz `HEAD` ao objecto e grava o que o R2 diz. Estão no schema porque
 * o registo tem de poder RECUSAR uma divergência antes de gravar, e porque um
 * corpo sem eles seria um corpo que não afirma nada.
 */
export const RegisterNoteAttachmentSchema = z.object({
  r2Key: z.string().min(1).max(512),
  filename: z.string().min(1).max(NOTE_ATTACHMENT_NAME_MAX_LENGTH),
  contentType: NoteAttachmentContentTypeSchema,
  sizeBytes: z.number().int().positive(),
})
export type RegisterNoteAttachment = z.infer<typeof RegisterNoteAttachmentSchema>

/**
 * O anexo tal como as três APIs o devolvem (as duas internas e a do portal).
 *
 * CONJUNTO FECHADO, e a mesma forma dos dois lados de propósito: o cliente vê o
 * mesmo que a Genesis sobre um arquivo que ele já pode ler. O que os separa é o
 * `WHERE` que decide SE ele o pode ler, e só isso.
 *
 * ⚠️ `r2Key` NÃO está aqui, e a ausência é deliberada: a chave é a coordenada do
 * objecto no bucket e não tem por que atravessar para browser nenhum. Quem a
 * quisesse usar teria na mesma de passar pela rota, que verifica o predicado — mas
 * publicá-la seria dar a quem olha o mapa do prefixo, e o prefixo é previsível por
 * profissional. `uploadedBy` (o id do usuário) também fica fora, pela mesma razão que
 * `NoteView.authorUserId`: o que a tela precisa é do NOME.
 */
export interface NoteAttachmentView {
  id: string
  noteId: string
  filename: string
  contentType: string
  sizeBytes: number
  createdAt: string
  /** `null` quando quem anexou já não existe (o `LEFT JOIN` não inventa um nome). */
  uploadedByName: string | null
}

// ============================================================
// PORTAL DO CLIENTE, F3 — A VITRINE (migration 0035)
// Spec: docs/superpowers/specs/2026-08-05-portal-do-cliente-design.md
//       §3.1 (o schema), §3.2 (`presented_at`) e §5 (fluxos 2 e 4).
// ============================================================

/**
 * A reação do cliente a um perfil da vitrine (§5, fluxo 4).
 *
 * Dois valores e mais nenhum. Não há `maybe` nem `later` de propósito: um sinal
 * que não decide nada não é um sinal — é ruído numa linha do tempo que a Genesis
 * lê para saber o que fazer a seguir. Espelha o enum de
 * `application_events.client_signal` (sem `CHECK` no D1, mesma postura da 0025).
 */
export const ClientSignalSchema = z.enum(['interested', 'declined'])
export type ClientSignal = z.infer<typeof ClientSignalSchema>

/**
 * O corpo do sinal do cliente.
 *
 * ⚠️ SEM `.strict()`, e pela MESMA razão escrita por extenso em
 * `CreateAllocationNoteSchema`: os campos que o servidor impõe — `type`,
 * `applicationId`, `createdBy` — não são DECLARADOS aqui, portanto o zod
 * descarta-os e nem um `...parsed.data` distraído no `insert` os faria chegar à
 * tabela. A garantia que interessa é a que sobrevive ao refactor, não a que
 * depende de o `.strict()` continuar lá.
 *
 * `note` é opcional porque a maior parte dos sinais é um clique. Quando existe, é
 * o que o cliente quis dizer para além do botão — e vai para a coluna `note` que
 * `application_events` já tem, sem coluna nova nenhuma.
 */
export const CreateClientSignalSchema = z.object({
  signal: ClientSignalSchema,
  note: z.string().min(1).max(2000).optional(),
})
export type CreateClientSignal = z.infer<typeof CreateClientSignalSchema>

/**
 * O corpo do sinal pelo CATÁLOGO PÚBLICO (Story 23.20). Quem clica é um
 * visitante sem conta, com o token da vaga.
 *
 * ⚠️ SEM `note`, e com `.strict()` para que um `note` enviado seja RECUSADO (400)
 * em vez de descartado em silêncio: texto livre de um visitante anónimo na linha
 * do tempo interna é um vetor de abuso que ninguém pediu. A rota grava
 * `note: null` como literal na mesma, portanto tirar o `.strict()` não reabre a
 * porta, só deixa de a anunciar.
 *
 * O `applicationId` vai no CORPO e não no caminho porque o caminho já é do
 * token (`/:token/signal`), e o token é o tenant.
 */
export const CreatePublicCatalogSignalSchema = z
  .object({
    applicationId: z.string().min(1),
    signal: ClientSignalSchema,
  })
  .strict()
export type CreatePublicCatalogSignal = z.infer<typeof CreatePublicCatalogSignalSchema>

/**
 * Pôr (ou tirar) uma candidatura da vitrine — o ATO DE CURADORIA da §3.2.
 *
 * Um booleano, e não uma data: quem apresenta não escolhe QUANDO apresentou, e
 * deixar o instante vir do cliente HTTP abriria a porta a uma candidatura
 * "apresentada" no futuro ou antes de existir. O servidor carimba `new Date()`.
 *
 * `.strict()` aqui — ao contrário dos dois acima — porque não há invariante
 * nenhum que um campo a mais possa tentar contornar: só existe um campo, e
 * recusar o desconhecido não esconde nada.
 */
export const SetPresentedSchema = z.object({ presented: z.boolean() }).strict()
export type SetPresented = z.infer<typeof SetPresentedSchema>

/**
 * Ação em massa sobre candidaturas, a partir da seleção na listagem.
 *
 * O teto de 100 ids existe por causa do D1 (100 parâmetros por instrução): a
 * rota fatia as consultas, mas um pedido sem teto seria um pedido sem fim.
 * Cada id recebe o seu desfecho: apresentar uma candidatura sem perfil pronto
 * não derruba o lote, volta como `profile_not_ready` para a tela dizer quantas.
 */
export const BULK_APPLICATION_ACTIONS = ['present', 'unpresent', 'archive', 'unarchive'] as const
export const BulkApplicationActionSchema = z
  .object({
    action: z.enum(BULK_APPLICATION_ACTIONS),
    ids: z
      .array(z.string().min(1))
      .min(1)
      .max(100)
      .refine((ids) => new Set(ids).size === ids.length, 'ids repetidos'),
  })
  .strict()
export type BulkApplicationAction = z.infer<typeof BulkApplicationActionSchema>
export type BulkApplicationActionFailure = 'not_found' | 'profile_not_ready'
export type BulkApplicationActionResult =
  | { id: string; ok: true }
  | { id: string; ok: false; reason: BulkApplicationActionFailure }
export interface BulkApplicationActionResponse {
  results: BulkApplicationActionResult[]
}

/**
 * Criar candidaturas numa vaga a partir da SELEÇÃO na lista de profissionais
 * (Story 31.9, Epic 31). O teto de 100 é o do lote de cima, pela mesma razão.
 *
 * Cada profissional recebe o seu desfecho, na ordem do pedido, e uma linha que
 * falha não derruba as outras:
 *   • `already_applied` — já tem candidatura nesta vaga (saltado, não duplicado);
 *   • `archived` — o profissional está arquivado;
 *   • `not_found` — o profissional não existe;
 *   • `failed` — a escrita desta linha falhou por outra razão (o D1 não respondeu).
 *     As outras seguem: uma linha não derruba o lote.
 * A candidatura criada é a mesma da rota unitária, campo a campo.
 */
export const BULK_CREATE_APPLICATIONS_MAX = 100
export const BulkCreateApplicationsSchema = z
  .object({
    opportunityId: z.string().min(1).max(64),
    professionalIds: z
      .array(z.string().min(1).max(64))
      .min(1)
      .max(BULK_CREATE_APPLICATIONS_MAX)
      .refine((ids) => new Set(ids).size === ids.length, 'ids repetidos'),
  })
  .strict()
export type BulkCreateApplications = z.infer<typeof BulkCreateApplicationsSchema>
export type BulkCreateApplicationsFailure = 'already_applied' | 'archived' | 'not_found' | 'failed'
export type BulkCreateApplicationsResult =
  | { professionalId: string; ok: true; applicationId: string }
  | { professionalId: string; ok: false; reason: BulkCreateApplicationsFailure }
export interface BulkCreateApplicationsResponse {
  opportunityId: string
  results: BulkCreateApplicationsResult[]
}

/**
 * Os destinatários de um e-mail de partilha de uma vaga (Story 23.15): os
 * e-mails cadastrados na vaga em `to`, e quem enviou em `cc` (vazio quando já
 * está na lista, ou quando a vaga não tem e-mails e o envio foi só para ele).
 */
export interface ShareEmailRecipients {
  to: string[]
  cc: string[]
}

/**
 * Enviar por e-mail os RELATÓRIOS DE ENTREVISTA de uma ou mais candidaturas —
 * um e-mail só, para os e-mails da vaga (Story 23.15). Todas têm de ser da
 * MESMA vaga: é a lista dela que recebe.
 *
 * O teto é o do e-mail, não o do D1: vinte profissionais já é uma lista longa
 * para quem a lê, e cada um pode levar um CV anexo.
 */
export const APPLICATION_REPORT_EMAIL_MAX = 20
export const ApplicationReportEmailSchema = z
  .object({
    applicationIds: z
      .array(z.string().min(1))
      .min(1)
      .max(APPLICATION_REPORT_EMAIL_MAX)
      .refine((ids) => new Set(ids).size === ids.length, 'ids repetidos'),
  })
  .strict()
export type ApplicationReportEmail = z.infer<typeof ApplicationReportEmailSchema>
/**
 * Porque uma candidatura ficou de fora do e-mail: não existe, ou ainda não tem
 * relatório de entrevista (sem entrevista concluída, o link público dá 404).
 */
export type ApplicationReportEmailFailure = 'not_found' | 'no_report'
/**
 * O CV da candidatura: seguiu em anexo, não há CV, ou ficou de fora porque o
 * e-mail já ia no limite de tamanho.
 */
export type ApplicationReportEmailCv = 'attached' | 'none' | 'too_large'
export type ApplicationReportEmailResult =
  | { id: string; ok: true; linkActivated: boolean; cv: ApplicationReportEmailCv }
  | { id: string; ok: false; reason: ApplicationReportEmailFailure }
export interface ApplicationReportEmailResponse extends ShareEmailRecipients {
  results: ApplicationReportEmailResult[]
}
